//-----------------------------------------------------------
//
// This file was generated by tool.
// Any changes to this file will be overwritten.
//
//-----------------------------------------------------------

import { AutoSuggestFlamelDataSetTypes, EntryType } from '~/types';
import { CreditroKYCStatus } from '../app/pages/admin/central/types';
import {
    PaymentStatuses,
    SmartPayCandidatePayment,
    SmartPayPaymentSourceType,
} from '../app/pages/accounting/bank/smartpay/types';

export type CSharpObject = any;
export type CSharpDateTime = string;
export type CSharpGuid = string;

/**
 * @see Economic.InternalApi.Model.Company.SignupResponse+SignupValidationMessage
 *
 * @export
 * @interface SignupValidationMessage
 */
export interface SignupValidationMessage {
    code: string;
    message: string;
    developerHint: string;
    propertyName: string;
    attemptedValue: CSharpObject;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.BulkSendFromArchiveInformationResponse+InvoiceInfo
 *
 * @export
 * @interface InvoiceInfo
 */
export interface InvoiceInfo {
    invoiceId: number;
    canSend: boolean;
}

/**
 * @see Economic.InternalApi.Model.Product.ProductRequest+ProductPriceCurrency
 *
 * @export
 * @interface ProductPriceCurrency
 */
export interface ProductPriceCurrency {
    productNumber: string;
    currency: string;
    salesPrice: number;
}

/**
 * @see Economic.InternalApi.Model.CostTypeGroup+CostTypeGroupAccounts
 *
 * @export
 * @interface CostTypeGroupAccounts
 */
export interface CostTypeGroupAccounts {
    ongoing?: number;
    closed?: number;
}

/**
 * @see Economic.InternalApi.Model.Account+AccountDimension
 *
 * @export
 * @interface AccountDimension
 */
export interface AccountDimension {
    department?: number;
    isDepartmentMandatory: boolean;
    isUnitMandatory: boolean;
    unit1?: number;
    unit2?: number;
    mirrorCodeId?: number;
}

/**
 * @see Economic.InternalApi.Model.Account+AccountMultipleDimension
 *
 * @export
 * @interface AccountMultipleDimension
 */
export interface AccountMultipleDimension {
    isMandatory: boolean;
    dimensionId: number;
    dimensionKey?: number;
}

/**
 * @see Economic.InternalApi.Model.Account+AccountAccounts
 *
 * @export
 * @interface AccountAccounts
 */
export interface AccountAccounts {
    contraAccount?: number;
    totalFromAccount?: number;
    openingAccount?: number;
    realisationAccount?: number;
}

/**
 * @see Economic.InternalApi.Model.Accounting.SupplierInvoiceMatch
 *
 * @export
 * @interface SupplierInvoiceMatch
 */
export interface SupplierInvoiceMatch {
    supplierId: number;
    remainingAmount: number;
    currency: string;
}

/**
 * @see Economic.InternalApi.Model.Accounting.ReadOnlyDraftEntry
 *
 * @export
 * @interface ReadOnlyDraftEntry
 */
export interface ReadOnlyDraftEntry {
    setupId: number;
    journalId: number;
    entryId: number;
    entryType: number;
    date: Date;
    /**
     * This contains the Date formatted as dd.MM.yy
     * @type string
     * @memberof ReadOnlyDraftEntry
     */
    formattedDate: string;
    voucherNumber: number;
    text: string;
    amount: number;
    /**
     * This contains the Amount formatted like ###.###,##
     * @type string
     * @memberof ReadOnlyDraftEntry
     */
    formattedAmount: string;
    amountDKK: number;
    currency: string;
    exchangeRate?: number;
    accountNumber?: number;
    /**
     * This may contain a Supplier ID, a Customer ID, or Account Number, whichever is not null.
     * @type number
     * @memberof ReadOnlyDraftEntry
     */
    consolidatedAccountNumber?: number;
    accountName?: string;
    vatCode?: string;
    approved?: boolean;
    contraAccountNumber?: number;
    contraAccountName?: string;
    contraAccountVatCode?: string;
    customerNumber?: number;
    customerName?: string;
    supplierNumber?: number;
    supplierName?: string;
    department?: number;
    dimension1?: number;
    dimension2?: number;
    dimension3?: number;
    invoice?: string;
    costTypeId?: number;
    imageId?: number;
    projectId?: number;
    projectName?: string;
    hasPendingDocumentRequest?: boolean;
    documentRequestRequestedAt?: Date;
    documentRequestRequestedFromGuid?: string;
    documentRequestRequestedFromName?: string;
    unbalancedAmount?: number;
    paymentDetails?: PaymentDetails;
    dueDate?: Date;
    accruals?: Accruals;
}

/**
 * @see Economic.InternalApi.Model.Accounting.Assets
 *
 * @export
 * @interface Assets
 */
export interface Assets {
    groupNumber: number;
    number: number;
    startDate?: CSharpDateTime;
    usefulLife: number;
}

/**
 * @see Economic.InternalApi.Model.Accounting.Accruals
 *
 * @export
 * @interface Accruals
 */
export interface Accruals {
    accountNumber: number;
    endDate: CSharpDateTime;
    startDate?: CSharpDateTime;
}

/**
 * @see Economic.InternalApi.Model.Accounting.Project
 *
 * @export
 * @interface Project
 */
export interface Project {
    enabled: boolean;
    projectNumber: number;
    costTypeNumber: number;
    employeeNumber?: number;
}

/**
 * @see Economic.InternalApi.Model.Accounting.Dimension
 *
 * @export
 * @interface Dimension
 */
export interface Dimension {
    departmentNumber?: number;
    unit1?: number;
    quantity1?: number;
    unit2?: number;
    quantity2?: number;
}

/**
 * @see Economic.InternalApi.Model.Accounting.DistributionRow
 *
 * @export
 * @interface DistributionRow
 */
export interface DistributionRow {
    accountNumber: string;
    amount: number;
    department?: number;
    quantity1?: number;
    quantity2?: number;
    vatCode: string;
    debitCredit: boolean;
    grossNet: boolean;
    text: string;
    dimensionData: DimensionData[] | undefined;
}

/**
 * @see Economic.InternalApi.Model.Accounting.PaymentDetails
 *
 * @export
 * @interface PaymentDetails
 */
export interface PaymentDetails {
    type: number;
    ocrLine?: string;
    fiSupplierNo?: string;
    message?: string;
    giroAccount?: string;
    regNo?: string;
    accountNo?: string;
    ibanSwift?: string;
    iban?: string;
    bic?: string;
    cashAccount?: number;
    paymentAccount?: number;
    ocrLineMessage?: string;
    isExported?: boolean;
    isPaidByPaymentProvider?: boolean;
}

/**
 * @see Economic.InternalApi.Model.Accounting.JournalEntry
 *
 * @export
 * @interface JournalEntry
 */
export interface JournalEntry {
    entryId?: number;
    entryType: any; // Byte
    voucherNumber: number;
    date: CSharpDateTime;
    accountNumber?: number;
    vatCode: string;
    debitCredit: boolean;
    enterCharge?: boolean;
    amount: number;
    currency: string;
    exchangeRate?: number;
    contraAccountNumber?: number;
    contraVatCode: string;
    text: string;
    project?: Project;
    dimension?: Dimension;
    dimensionData?: DimensionData[];
    accruals?: Accruals;
    assets?: Assets;
    distributions: DistributionRow[];
    customerNumber?: number;
    customerInvoiceNumber?: string;
    supplierNumber?: number;
    supplierInvoiceNumber?: string;
    dueDate?: CSharpDateTime;
    paymentDetails?: PaymentDetails;
}

/**
 * @see Economic.InternalApi.Model.Accounting.MoveEntriesRequest
 *
 * @export
 * @interface MoveEntriesRequest
 */
export interface MoveEntriesRequest {
    destinationJournalId: number;
    entries: number[];
    useNewText: boolean;
    newText?: string;
    useNumberSeries: boolean;
    useAutomaticText: boolean;
}

/**
 * @see Economic.InternalApi.Model.Accounting.JournalVoucher
 *
 * @export
 * @interface JournalVoucher
 */
export interface JournalVoucher {
    journalId: number;
    accountingYear: string;
    voucherNumber?: number;
    documentNumber?: number;
    entries: JournalEntry[];
    setupId?: number;
}

/**
 * @see Economic.InternalApi.Model.Accounting.JournalBalanceLine
 *
 * @export
 * @interface JournalBalanceLine
 */
export interface JournalBalanceLine {
    balance: number;
    bankAccount: JournalBalanceLineItem;
    cashCreditAccount: JournalBalanceLineItem;
    interimAccount: JournalBalanceLineItem;
}

/**
 * @see Economic.Entity.JournalBalanceLineItem
 *
 * @export
 * @interface JournalBalanceLineItem
 */
export interface JournalBalanceLineItem {
    number: number;
    name: string;
    balance: number;
    balanceNumber: number;
}

/**
 * @see Economic.InternalApi.Model.Accounting.DraftEntryListPermissions
 *
 * @export
 * @interface DraftEntryListPermissions
 */
export interface DraftEntryListPermissions {
    approval: boolean;
    documentRequest: boolean;
    department: boolean;
    editApproval: boolean;
    project: boolean;
    lockVat: boolean;
    hasAccessAutoAttach: boolean;
    multiDimensions: boolean;
    invoicing: boolean;
}

/**
 * @see Economic.InternalApi.Model.Accounting.DraftEntriesDeleteInfo
 *
 * @export
 * @interface DraftEntriesDeleteInfo
 */
export interface DraftEntriesDeleteInfo {
    hasDocuments: boolean;
    smartPayPayments: SmartPayPaymentDetails[];
}

/**
 * @see Economic.InternalApi.Model.Accounting.CustomerInvoiceMatch
 *
 * @export
 * @interface CustomerInvoiceMatch
 */
export interface CustomerInvoiceMatch {
    customerId: number;
    remainingAmount: number;
    currency: string;
}

/**
 * @see Economic.InternalApi.Model.Accounting.SmartPayPaymentDetails
 *
 * @export
 * @interface SmartPayPaymentDetails
 */
export interface SmartPayPaymentDetails {
    date: CSharpDateTime;
    invoice: string;
    supplier: string;
    amount: number;
    currency: string;
}

/**
 * @see Economic.InternalApi.Model.Accounting.ConflictingVoucherNumberInfo
 *
 * @export
 * @interface ConflictingVoucherNumberInfo
 */
export interface ConflictingVoucherNumberInfo {
    voucherNumber: number;
    journalId: number;
    date: CSharpDateTime;
    accountingYearStartDate: CSharpDateTime;
    accountingYearEndDate: CSharpDateTime;
    hasAttachment: boolean;
}

/**
 * @see Economic.InternalApi.Model.Accounting.BookJournalEntriesRequest
 *
 * @export
 * @interface BookJournalEntriesRequest
 */
export interface BookJournalEntriesRequest {
    entryIds: number[];
    fromDate?: CSharpDateTime;
    toDate?: CSharpDateTime;
    resetAmount?: boolean;
    updateAttachmentOnVoucherConflict?: boolean;
}

/**
 * @see Economic.InternalApi.Model.Accounting.UpdateJournalEntriesVoucherNumberRequest
 *
 * @export
 * @interface UpdateJournalEntriesVoucherNumberRequest
 */
export interface UpdateJournalEntriesVoucherNumberRequest {
    entryIds: number[];
    fromDate?: CSharpDateTime;
    toDate?: CSharpDateTime;
    updateAttachmentOnVoucherConflict: boolean;
}

/**
 * @see Economic.InternalApi.Model.Accounting.BookJournalEntriesInformationResponse
 *
 * @export
 * @interface BookJournalEntriesInformationResponse
 */
export interface BookJournalEntriesInformationResponse {
    fromDate?: CSharpDateTime;
    toDate?: CSharpDateTime;
    conflictingVoucherNumbers: ConflictingVoucherNumberInfo[];
    nextAvailableVoucherNumber: number;
    warningMessage?: string;
}

/**
 * @see Economic.InternalApi.Model.AddonDeregisterFeedback.NewAddonDeregisterFeedback
 *
 * @export
 * @interface NewAddonDeregisterFeedback
 */
export interface NewAddonDeregisterFeedback {
    addonId: number;
    feedback: string;
    phoneNumber: string;
}

/**
 * @see Economic.InternalApi.Model.AddonDeregisterFeedback.DeleteAddonDeregisterFeedbackRecords
 *
 * @export
 * @interface DeleteAddonDeregisterFeedbackRecords
 */
export interface DeleteAddonDeregisterFeedbackRecords {
    ids: number[];
}

/**
 * @see Economic.InternalApi.Model.AddonDeregisterFeedback.AddonDeregisterFeedbackModel
 *
 * @export
 * @interface AddonDeregisterFeedbackModel
 */
export interface AddonDeregisterFeedbackModel {
    id: number;
    /**
     * @remarks
     * [TranslationKey]
     * @type number
     * @memberof AddonDeregisterFeedbackModel
     */
    agreementNumber: number;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof AddonDeregisterFeedbackModel
     */
    addon: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof AddonDeregisterFeedbackModel
     */
    userId: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof AddonDeregisterFeedbackModel
     */
    feedback: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof AddonDeregisterFeedbackModel
     */
    phoneNumber: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof AddonDeregisterFeedbackModel
     */
    userRole: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type CSharpDateTime
     * @memberof AddonDeregisterFeedbackModel
     */
    creationDateTime: CSharpDateTime;
    /**
     * @remarks
     * [TranslationKey]
     * @type CSharpDateTime
     * @memberof AddonDeregisterFeedbackModel
     */
    registrationDate: CSharpDateTime;
}

/**
 * @see Economic.InternalApi.Model.Addon.AddonAccess
 *
 * @export
 * @interface AddonAccess
 */
export interface AddonAccess {
    id: number;
    textId: string;
}

/**
 * @see Economic.InternalApi.Model.Attributes.ZeroOrGreaterAttribute
 *
 * @export
 * @interface ZeroOrGreaterAttribute
 */
export interface ZeroOrGreaterAttribute {}

/**
 * Validation taken from https://github.com/FluentValidation/FluentValidation/blob/main/src/FluentValidation/Validators/ScalePrecisionValidator.cs
 * @see Economic.InternalApi.Model.Attributes.ScalePrecisionValueAttribute
 *
 * @export
 * @interface ScalePrecisionValueAttribute
 */
export interface ScalePrecisionValueAttribute {
    scale: number;
    precision: number;
    ignoreTrailingZeros: boolean;
}

/**
 * @see Economic.InternalApi.Model.Attributes.GreaterThanZeroAttribute
 *
 * @export
 * @interface GreaterThanZeroAttribute
 */
export interface GreaterThanZeroAttribute {}

/**
 * @see Economic.InternalApi.Model.Attributes.AssetGroupNumberAttribute
 *
 * @export
 * @interface AssetGroupNumberAttribute
 */
export interface AssetGroupNumberAttribute {}

/**
 * @see Economic.InternalApi.Model.Attributes.AccountNumberAttribute
 *
 * @export
 * @interface AccountNumberAttribute
 */
export interface AccountNumberAttribute {}

/**
 * @see Economic.InternalApi.Model.Bank.SuggestionModel
 *
 * @export
 * @interface SuggestionModel
 */
export interface SuggestionModel {
    fetchingDate?: CSharpDateTime;
    paymentAccountNumber?: number;
    currency: string;
}

/**
 * @see Economic.InternalApi.Model.Bank.SettingsModel
 *
 * @export
 * @interface SettingsModel
 */
export interface SettingsModel {
    hasOnboarded: boolean;
    settingsUrl: string;
    authUrl: string;
    hasOtherBankIntegration: boolean;
    isManualSyncAllowed: boolean;
    isAccessTokenValid?: boolean;
    hasMoreAiiaIntegrations: boolean;
    aiiaAccountFixRequired: boolean;
}

/**
 * @see Economic.InternalApi.Model.Bank.SendEmailModel
 *
 * @export
 * @interface SendEmailModel
 */
export interface SendEmailModel {
    emailTo: string;
    emailCC: string;
    emailBCC: string;
    subject: string;
    body: string;
    remember: boolean;
    language: SmartBankEmailTemplateLanguageEnum;
    signature: string;
}

/**
 * @see EconomicEnum.SmartBankEmailTemplateLanguage
 *
 * @export
 * @enum {number}
 */
export enum SmartBankEmailTemplateLanguageEnum {
    Danish = 1,
    English = 2,
    Custom = 3,
}

/**
 * @see Economic.InternalApi.Model.Bank.PairAccountsPostRequest
 *
 * @export
 * @interface PairAccountsPostRequest
 */
export interface PairAccountsPostRequest {
    accountId: number;
    pairingAccountNumber: number;
    fetchingDate: CSharpDateTime;
}

/**
 * @see Economic.InternalApi.Model.Bank.ExternalOnboardingSettingsModel
 *
 * @export
 * @interface ExternalOnboardingSettingsModel
 */
export interface ExternalOnboardingSettingsModel {
    company: string;
    authUrl: string;
    hasOnboarded: boolean;
    settingsUrl: string;
    isConsentRevoked: boolean;
    isManualSyncAllowed: boolean;
    isAccessTokenValid: boolean;
    hasMoreAiiaIntegrations: boolean;
}

/**
 * @see Economic.InternalApi.Model.Bank.ExternalOnboardingInfoModel
 *
 * @export
 * @interface ExternalOnboardingInfoModel
 */
export interface ExternalOnboardingInfoModel {
    invitationUrl: string;
    email: string;
    sentAt?: CSharpDateTime;
    subject: string;
    body: string;
    signature: string;
    templates: EmailTemplateModel[];
}

/**
 * @see Economic.InternalApi.Model.Bank.EmailTemplateModel
 *
 * @export
 * @interface EmailTemplateModel
 */
export interface EmailTemplateModel {
    language: SmartBankEmailTemplateLanguageEnum;
    subject: string;
    body: string;
    signature: string;
}

/**
 * @see Economic.InternalApi.Model.Bank.ExternalInvitationEmailPreviewModel
 *
 * @export
 * @interface ExternalInvitationEmailPreviewModel
 */
export interface ExternalInvitationEmailPreviewModel {
    tokenId: number;
    adminSetupId: number;
    bOSetupId: number;
    emailAddress: string;
    sentAt: CSharpDateTime;
    subject: string;
    body: string;
    signature: string;
}

/**
 * @see Economic.InternalApi.Model.Bank.ExternalBankModel
 *
 * @export
 * @interface ExternalBankModel
 */
export interface ExternalBankModel {
    name: string;
    code: string;
    connectionExpiry?: CSharpDateTime;
    accounts: ExternalAccountModel[];
}

/**
 * @see Economic.InternalApi.Model.Bank.ExternalAccountModel
 *
 * @export
 * @interface ExternalAccountModel
 */
export interface ExternalAccountModel {
    id: number;
    accountNumber: string;
    accountName: string;
    from?: CSharpDateTime;
    isPairedByAdmin: boolean;
    isConsentActive?: boolean;
    isOrphaned: boolean;
}

/**
 * @see Economic.InternalApi.Model.Bank.EmailPostRequest
 *
 * @export
 * @interface EmailPostRequest
 */
export interface EmailPostRequest {
    email: string;
}

/**
 * @see Economic.InternalApi.Model.Bank.ConsentPostRequest
 *
 * @export
 * @interface ConsentPostRequest
 */
export interface ConsentPostRequest {
    code: string;
    consentId: string;
    redirectUri: string;
}

/**
 * @see Economic.InternalApi.Model.Bank.BankModel
 *
 * @export
 * @interface BankModel
 */
export interface BankModel {
    name: string;
    code: string;
    connectionExpiry?: CSharpDateTime;
    accounts: AccountModel[];
}

/**
 * @see Economic.InternalApi.Model.Bank.AccountModel
 *
 * @export
 * @interface AccountModel
 */
export interface AccountModel {
    id: number;
    accountNumber: string;
    accountName: string;
    from?: CSharpDateTime;
    to?: CSharpDateTime;
    lastSync?: CSharpDateTime;
    paymentAccountNumber?: number;
    paymentAccountName: string;
    smartBankAccountId: string;
    bankName: string;
    isConsentActive?: boolean;
    lastSupervisedLogin: CSharpDateTime;
    isOrphaned: boolean;
    bookedBalance: number;
}

/**
 * @see Economic.InternalApi.Model.Bank.AccountDeleteRequest
 *
 * @export
 * @interface AccountDeleteRequest
 */
export interface AccountDeleteRequest {
    accountId: number;
}

/**
 * @see Economic.InternalApi.Model.Budget.BudgetPeriod
 *
 * @export
 * @interface BudgetPeriod
 */
export interface BudgetPeriod {
    setupId: number;
    id: number;
    accountNumber: number;
    fromDate?: CSharpDateTime;
    toDate?: CSharpDateTime;
    text: string;
    amount: number;
    unit1?: number;
    quantity1?: number;
    unit2?: number;
    quantity2?: number;
    department?: number;
}

/**
 * Mapped to  inside
 * @see Economic.InternalApi.Model.Company.TrialSignupModel
 *
 * @export
 * @interface TrialSignupModel
 */
export interface TrialSignupModel {
    trialMasterAgreementNumber: number;
    phone: string;
    kissMetricCustomerId: string;
    marketoId: string;
    mixpanelId: string;
    campaignCode: string;
    demoDays?: number;
    signupSource: string;
    resellerLead?: number;
    partnerAgreementNumber?: number;
    skipSendingWelcomeEmail: boolean;
}

/**
 * @see Economic.InternalApi.Model.Company.TrialExtensionResponse
 *
 * @export
 * @interface TrialExtensionResponse
 */
export interface TrialExtensionResponse {
    expiryDate?: CSharpDateTime;
}

/**
 * @see Economic.InternalApi.Model.Company.SignupResponse
 *
 * @export
 * @interface SignupResponse
 */
export interface SignupResponse {
    redirectURL: string;
}

/**
 * @see Economic.InternalApi.Model.Company.SignupModelBase
 *
 * @export
 * @interface SignupModelBase
 */
export interface SignupModelBase {
    companyName: string;
    eMail: string;
    firstName: string;
    lastName: string;
    useVismaConnect: boolean;
    skipMagicLinkGeneration: boolean;
}

/**
 * Mapped to  inside
 * @see Economic.InternalApi.Model.Company.DeveloperSignupModel
 *
 * @export
 * @interface DeveloperSignupModel
 */
export interface DeveloperSignupModel {}

/**
 * @see Economic.InternalApi.Model.Company.AddOnModel
 *
 * @export
 * @interface AddOnModel
 */
export interface AddOnModel {
    id: number;
    discountPct?: number;
    discountExpiry?: CSharpDateTime;
    campaignAuthor: string;
    campaignName: string;
}

/**
 * Mapped to  inside
 * @see Economic.InternalApi.Model.Company.CompanySignupModel
 *
 * @export
 * @interface CompanySignupModel
 */
export interface CompanySignupModel {
    economicEditionId: number;
    planId: number;
    phone: string;
    address: string;
    zipCode: string;
    city: string;
    cvrNumber: string;
    signupSource: string;
    kissMetricCustomerId: string;
    marketoId: string;
    mixpanelId: string;
    campaignCode: string;
    resellerLead?: number;
    firstInvoiceDate?: CSharpDateTime;
    addOns: AddOnModel[];
    registeredByAdmin?: number;
}

/**
 * @see Economic.InternalApi.Model.Company.CompanyDeregisterDataModel
 *
 * @export
 * @interface CompanyDeregisterDataModel
 */
export interface CompanyDeregisterDataModel {
    archiveSubscriptionPrice: string;
    archiveSubscriptionStartDate: string;
    archiveSubscriptionEndDate: string;
    archiveSubscriptionDataDeletionDate: string;
    deregistrationDate: string;
    dataDeletionDate: string;
    recommendationScale: any[]; // List<KeyValuePair<String, String>>
    changeOfAccountingProgramReasons: any[]; // List<KeyValuePair<String, String>>
    contactPhone: string;
    learnMoreLink: string;
    skipArchiveLicenseChoice: boolean;
}

/**
 * @see Economic.InternalApi.Model.Company.BulkCredit
 *
 * @export
 * @interface BulkCredit
 */
export interface BulkCredit {
    agreementNumber: number;
    invoices: number[];
    reminders: number[];
}

/**
 * @see Economic.InternalApi.Model.CustomerLedgerCards.SingleCustomerLedgerCardSendingResponse
 *
 * @export
 * @interface SingleCustomerLedgerCardSendingResponse
 */
export interface SingleCustomerLedgerCardSendingResponse {
    pdfLink: string;
}

/**
 * @see Economic.InternalApi.Model.CustomerLedgerCards.SingleCustomerLedgerCardSendingRequest
 *
 * @export
 * @interface SingleCustomerLedgerCardSendingRequest
 */
export interface SingleCustomerLedgerCardSendingRequest {
    customerId: number;
    heading: string;
    text: string;
    currency: string;
    fromDate: CSharpDateTime;
    toDate: CSharpDateTime;
    onlyOpen: boolean;
    layoutGroup: number;
    subject: string;
    saveSubject: boolean;
    body: string;
    saveBody: boolean;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    showAfterSending: boolean;
}

/**
 * @see Economic.InternalApi.Model.CustomerLedgerCards.SingleCustomerLedgerCardSendingInformation
 *
 * @export
 * @interface SingleCustomerLedgerCardSendingInformation
 */
export interface SingleCustomerLedgerCardSendingInformation {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    showAfterSending: boolean;
    exceededSendingLimit: boolean;
}

/**
 * @see Economic.InternalApi.Model.CustomerLedgerCards.LedgerCardCustomer
 *
 * @export
 * @interface LedgerCardCustomer
 */
export interface LedgerCardCustomer {
    customerNumber: number;
    name: string;
}

/**
 * @see Economic.InternalApi.Model.CustomerLedgerCards.BulkCustomerLedgerCardsSendingResponse
 *
 * @export
 * @interface BulkCustomerLedgerCardsSendingResponse
 */
export interface BulkCustomerLedgerCardsSendingResponse {
    processedCustomerIds: number[];
    pdfLink: string;
}

/**
 * @see Economic.InternalApi.Model.CustomerLedgerCards.BulkCustomerLedgerCardsSendingRequest
 *
 * @export
 * @interface BulkCustomerLedgerCardsSendingRequest
 */
export interface BulkCustomerLedgerCardsSendingRequest {
    customerIds: number[];
    heading: string;
    text: string;
    currency: string;
    fromDate: CSharpDateTime;
    toDate: CSharpDateTime;
    onlyOpen: boolean;
    layoutGroup: number;
    subject: string;
    saveSubject: boolean;
    body: string;
    saveBody: boolean;
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    showAfterSending: boolean;
}

/**
 * @see Economic.InternalApi.Model.CustomerLedgerCards.BulkCustomerLedgerCardsSendingInformation
 *
 * @export
 * @interface BulkCustomerLedgerCardsSendingInformation
 */
export interface BulkCustomerLedgerCardsSendingInformation {
    subject: string;
    body: string;
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    showAfterSending: boolean;
    validCustomerIds: number[];
    exceededSendingLimit: boolean;
    customersWithInvalidEmails: LedgerCardCustomer[];
}

/**
 * @see Economic.InternalApi.Model.Customer.CustomerContact
 *
 * @export
 * @interface CustomerContact
 */
export interface CustomerContact {
    id: number;
    userInterfaceId: number;
    name: string;
    receiveQuotations: boolean;
    receiveOrders: boolean;
    receiveInvoices: boolean;
    receiveEInvoices: boolean;
    receiveDeliveryNotes: boolean;
    receiveStatementOfAccounts: boolean;
    receiveReminders: boolean;
    receivePurchaseOrders: boolean;
    phone: string;
    email: string;
    note: string;
    externalId: string;
}

/**
 * @see Economic.InternalApi.Model.Customer.Customer
 *
 * @export
 * @interface Customer
 */
export interface Customer {
    customerNumber: number;
    name: string;
}

/**
 * @see Economic.InternalApi.Model.CustomerGlnNumberViewModel
 *
 * @export
 * @interface CustomerGlnNumberViewModel
 */
export interface CustomerGlnNumberViewModel {
    id: number;
    customerId: number;
    gln: string;
    name: string;
}

/**
 * @see Economic.InternalApi.Model.Customer.AutoSuggestSimpleCustomer
 *
 * @export
 * @interface AutoSuggestSimpleCustomer
 */
export interface AutoSuggestSimpleCustomer {
    customerNumber: number;
    name: string;
    currency: string;
}

/**
 * @see Economic.InternalApi.Model.Daybooks.FieldOrderSetup
 *
 * @export
 * @interface FieldOrderSetup
 */
export interface FieldOrderSetup {
    countryLevelOptions: FieldOrder[];
    userLevelOptions: FieldOrder[];
    currentSetup: DaybookUserFieldOrderSetup[];
}

/**
 * @see Economic.InternalApi.Model.Daybooks.DaybookUserFieldOrderSetup
 *
 * @export
 * @interface DaybookUserFieldOrderSetup
 */
export interface DaybookUserFieldOrderSetup {
    daybookNumber: number;
    userFieldOrderNumber?: number;
    countryFieldOrderNumber?: number;
}

/**
 * @see Economic.InternalApi.Model.Daybooks.FieldOrder
 *
 * @export
 * @interface FieldOrder
 */
export interface FieldOrder {
    number: number;
    name: string;
}

/**
 * @see Economic.InternalApi.Model.DesignAndLayout.UpdateFontRequest
 *
 * @export
 * @interface UpdateFontRequest
 */
export interface UpdateFontRequest {
    invoiceLayoutNumber: number;
    fontId: number;
    maintainOcrFont: boolean;
    changeElements: boolean;
    changeColumns: boolean;
}

/**
 * @see Economic.InternalApi.Model.DesignAndLayout.OverwriteInvoiceLayoutRequest
 *
 * @export
 * @interface OverwriteInvoiceLayoutRequest
 */
export interface OverwriteInvoiceLayoutRequest {
    sourceInvoiceLayoutNumber: number;
    targetInvoiceLayoutNumber: number;
}

/**
 * @see Economic.InternalApi.Model.DesignAndLayout.MoveLayoutElementsRequest
 *
 * @export
 * @interface MoveLayoutElementsRequest
 */
export interface MoveLayoutElementsRequest {
    invoiceLayoutNumber: number;
    moveX: number;
    moveY: number;
    elements: number[];
}

/**
 * @see Economic.InternalApi.Model.DesignAndLayout.ChangeZIndexRequest
 *
 * @export
 * @interface ChangeZIndexRequest
 */
export interface ChangeZIndexRequest {
    invoiceLayoutNumber: number;
    currentZIndex: number;
    newZIndex: number;
}

/**
 * @see Economic.InternalApi.Model.DesignAndLayout.MoveLayoutColumnRequest
 *
 * @export
 * @interface MoveLayoutColumnRequest
 */
export interface MoveLayoutColumnRequest {
    invoiceLayoutNumber: number;
    currentColumnNumber: number;
    newColumnNumber: number;
}

/**
 * @see Economic.InternalApi.Model.DesignAndLayout.InvoiceLayoutType
 *
 * @export
 * @interface InvoiceLayoutType
 */
export interface InvoiceLayoutType {
    invoiceLayoutNumber: number;
    name: string;
    type: number;
}

/**
 * @see Economic.InternalApi.Model.DesignAndLayout.InvoiceLayoutStandardPage
 *
 * @export
 * @interface InvoiceLayoutStandardPage
 */
export interface InvoiceLayoutStandardPage {
    width: number;
    height: number;
    nameTextId: string;
}

/**
 * @see Economic.InternalApi.Model.DesignAndLayout.InvoiceLayoutImage
 *
 * @export
 * @interface InvoiceLayoutImage
 */
export interface InvoiceLayoutImage {
    id: number;
    name: string;
    width: number;
    height: number;
}

/**
 * @see Economic.InternalApi.Model.DesignAndLayout.InvoiceLayout
 *
 * @export
 * @interface InvoiceLayout
 */
export interface InvoiceLayout {
    id: number;
    groupId: number;
    lineHeight: number;
    width: number;
    height: number;
    pageSizeName: string;
    useLetterHead: boolean;
    includeInvoices: boolean;
    layoutType?: any; // Nullable<Byte>
    name: string;
    letterHeadLayoutId?: number;
    extraDocumentId?: number;
    watermarkForInvoices: string;
    headerText: string;
    maxColumnWidth: number;
}

/**
 * @see Economic.InternalApi.Model.DesignAndLayout.Font
 *
 * @export
 * @interface Font
 */
export interface Font {
    id: number;
    name: string;
}

/**
 * @see Economic.InternalApi.Model.DesignAndLayout.Element
 *
 * @export
 * @interface Element
 */
export interface Element {
    id: number;
    elementType: any; // Byte
    xPos: number;
    yPos: number;
    width: number;
    height: number;
    borderWidthLeft: number;
    borderWidthRight: number;
    borderWidthTop: number;
    borderWidthBottom: number;
    lineBreaks: boolean;
    text: string;
    fontId?: number;
    underline?: boolean;
    fontSize?: number;
    color: string;
    backgroundColor: string;
    alignment?: any; // Nullable<Byte>
    imageId?: number;
    borderColor: string;
    lineHeight?: number;
    marginLeft?: number;
    marginRight?: number;
    marginTop?: number;
    marginBottom?: number;
    leadingDots?: boolean;
    position?: any; // Nullable<Byte>
    borderWidthTopOver?: number;
    linkToUrl: string;
    fontIsBold: boolean;
    fontIsItalics: boolean;
    canChangeToPaymentLink: boolean;
    imageName: string;
}

/**
 * @see Economic.InternalApi.Model.DesignAndLayout.DynamicField
 *
 * @export
 * @interface DynamicField
 */
export interface DynamicField {
    name: string;
}

/**
 * @see Economic.InternalApi.Model.DesignAndLayout.ColumnWidthWithinBoundsValidationResult
 *
 * @export
 * @interface ColumnWidthWithinBoundsValidationResult
 */
export interface ColumnWidthWithinBoundsValidationResult {
    valid: boolean;
    availableWidth: number;
}

/**
 * @see Economic.InternalApi.Model.DesignAndLayout.Column
 *
 * @export
 * @interface Column
 */
export interface Column {
    /**
     * @remarks
     * [Required]: The Color field is required.
     * @type string
     * @memberof Column
     */
    color: string;
    /**
     * @remarks
     * [GreaterThanZero]: E04710
     * @type number
     * @memberof Column
     */
    fontSize: number;
    /**
     * @remarks
     * [ZeroOrGreater]: The field HeadlineSpacingAbove is invalid.
     * @type number
     * @memberof Column
     */
    headlineSpacingAbove: number;
    /**
     * @remarks
     * [ZeroOrGreater]: The field HeadlineMargin is invalid.
     * @type number
     * @memberof Column
     */
    headlineMargin: number;
    /**
     * @remarks
     * [ZeroOrGreater]: The field NumberOfDecimals is invalid.
     * @type any
     * @memberof Column
     */
    numberOfDecimals?: any; // Nullable<Byte>
    id: any; // Byte
    priority: any; // Byte
    width: number;
    marginLeft: number;
    marginRight: number;
    fontId: number;
    fontName: string;
    underline: boolean;
    alignment: any; // Byte
    headlineFontId: number;
    headlineUnderline: boolean;
    headlineFontSize: number;
    headlineColor: string;
    headlineAlignment: any; // Byte
    hideHeadlineWhenEmpty: boolean;
    borderWidthLeft: number;
    borderWidthRight: number;
    show: boolean;
    hideWhenBlank: boolean;
    verticalAlignment: any; // Byte
    lineBreaks: boolean;
    backgroundColor: string;
    textBefore: string;
    textAfter: string;
    borderColor: string;
    headline: string;
    fontIsBold: boolean;
    fontIsItalics: boolean;
    headlineFontIsBold: boolean;
    headlineFontIsItalics: boolean;
    alignmentText: string;
    continueBorder: boolean;
    canBeHiddenType: boolean;
    canToggleLinebreaks: boolean;
    hasDecimals: boolean;
    columnName: string;
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.UpdateNoteModel
 *
 * @export
 * @interface UpdateNoteModel
 */
export interface UpdateNoteModel {
    /**
     * @remarks
     * [Required]: RequiredField
     * [MaxLength]: The field Text must be a string or array type with a maximum length of '210'.
     * @type string
     * @memberof UpdateNoteModel
     */
    text: string;
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.TargetUserModel
 *
 * @export
 * @interface TargetUserModel
 */
export interface TargetUserModel {
    fullName: string;
    guid: string;
    hasDownloadedTheMobileApp: boolean;
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.SendDocumentRequestsModel
 *
 * @export
 * @interface SendDocumentRequestsModel
 */
export interface SendDocumentRequestsModel {
    /**
     * @remarks
     * [Required]: RequiredField
     * @type string
     * @memberof SendDocumentRequestsModel
     */
    requestedFromUserGuid: string;
    documentRequestIds: number[];
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.SendDocumentRequestModel
 *
 * @export
 * @interface SendDocumentRequestModel
 */
export interface SendDocumentRequestModel {
    documentRequestId: number;
    /**
     * @remarks
     * [Required]: RequiredField
     * @type string
     * @memberof SendDocumentRequestModel
     */
    requestedFromUserGuid: string;
    note: string;
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.RejectDocumentModel
 *
 * @export
 * @interface RejectDocumentModel
 */
export interface RejectDocumentModel {
    note: string;
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.IgnoreEntryModel
 *
 * @export
 * @interface IgnoreEntryModel
 */
export interface IgnoreEntryModel {
    journalId: number;
    entryId: number;
    note: string;
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.IgnoreEntriesModel
 *
 * @export
 * @interface IgnoreEntriesModel
 */
export interface IgnoreEntriesModel {
    entries: IgnoreEntryModel[];
    entriesWithoutRequests: IgnoreEntryModel[];
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.DraftEntryIdentifierModel
 *
 * @export
 * @interface DraftEntryIdentifierModel
 */
export interface DraftEntryIdentifierModel {
    journalId: number;
    entryId: number;
    /**
     * @remarks
     * [MaxLength]: FieldValueLengthTooLong
     * @type string
     * @memberof DraftEntryIdentifierModel
     */
    note: string;
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.DocumentRequestSettings
 *
 * @export
 * @interface DocumentRequestSettings
 */
export interface DocumentRequestSettings {
    /**
     * @remarks
     * [Required]: The AutoApproveDocuments field is required.
     * @type boolean
     * @memberof DocumentRequestSettings
     */
    autoApproveDocuments: boolean;
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.DocumentRequestNote
 *
 * @export
 * @interface DocumentRequestNote
 */
export interface DocumentRequestNote {
    documentRequestId: number;
    id: number;
    createdByUserGuid: string;
    userFullName: string;
    text: string;
    createdAt: CSharpDateTime;
    sentAt?: CSharpDateTime;
    isResponse: boolean;
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.DocumentRequestEntryFilters
 *
 * @export
 * @interface DocumentRequestEntryFilters
 */
export interface DocumentRequestEntryFilters {
    documentRequestStatus: DocumentRequestStatusEnum[];
}

/**
 * @see Economic.Entity.DocumentRequest.DocumentRequestStatus
 *
 * @export
 * @enum {number}
 */
export enum DocumentRequestStatusEnum {
    Draft = 0,
    Pending = 1,
    DocumentUploaded = 2,
    Rejected = 3,
    Cancelled = 4,
    Ignored = 5,
    Completed = 10,
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.DocumentRequestEntryExport
 *
 * @export
 * @interface DocumentRequestEntryExport
 */
export interface DocumentRequestEntryExport {
    /**
     * @remarks
     * [TranslationKey]
     * @type EntryTypeNamesEnum
     * @memberof DocumentRequestEntryExport
     */
    entryType: EntryTypeNamesEnum;
    /**
     * @remarks
     * [TranslationKey]
     * @type CSharpDateTime
     * @memberof DocumentRequestEntryExport
     */
    date: CSharpDateTime;
    /**
     * @remarks
     * [TranslationKey]
     * @type number
     * @memberof DocumentRequestEntryExport
     */
    voucherNumber?: number;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof DocumentRequestEntryExport
     */
    text: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type number
     * @memberof DocumentRequestEntryExport
     */
    amount: number;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof DocumentRequestEntryExport
     */
    vatCode: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type number
     * @memberof DocumentRequestEntryExport
     */
    accountId?: number;
    /**
     * @remarks
     * [TranslationKey]
     * @type number
     * @memberof DocumentRequestEntryExport
     */
    contraAccountId?: number;
    /**
     * @remarks
     * [TranslationKey]
     * @type DocumentRequestStatusEnum
     * @memberof DocumentRequestEntryExport
     */
    documentRequestStatus?: DocumentRequestStatusEnum;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof DocumentRequestEntryExport
     */
    note: string;
}

/**
 * @see Economic.Constants.EntryTypeNames
 *
 * @export
 * @enum {number}
 */
export enum EntryTypeNamesEnum {
    All = 0,
    CustomerInvoice = 1,
    CustomerPayment = 2,
    SupplierInvoice = 3,
    SupplierPayment = 4,
    FinanceVoucher = 5,
    Reminder = 6,
    OpeningEntry = 7,
    TransferredOpeningEntry = 8,
    SystemEntry = 9,
    ManualCustomerInvoice = 10,
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.DocumentRequestEntry
 *
 * @export
 * @interface DocumentRequestEntry
 */
export interface DocumentRequestEntry {
    journalId: number;
    entryId: number;
    entryType: any; // Byte
    date: CSharpDateTime;
    amountInBaseCurrency: number;
    currency: string;
    amount: number;
    accountId?: number;
    voucherNumber?: number;
    text: string;
    vatCode: string;
    contraAccountId?: number;
    entryStatus: EntryStatusEnum;
    documentRequest: DocumentRequest;
    supplierId?: number;
    supplierName: string;
    accountingYearName: string;
    requestAttachedToOtherVoucherLeg: boolean;
    hasOtherVoucherLegs: boolean;
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.DocumentRequest
 *
 * @export
 * @interface DocumentRequest
 */
export interface DocumentRequest {
    id: number;
    status: DocumentRequestStatusEnum;
    entryStatus: EntryStatusEnum;
    requestedByUserGuid: string;
    requestedAt?: CSharpDateTime;
    requestedFromUserGuid: string;
    accountingYear: string;
    accountingYearFrom?: CSharpDateTime;
    accountingYearTo?: CSharpDateTime;
    voucherNumber?: number;
    entryId?: number;
    journalId?: number;
    entry: DocumentRequestEntry;
    notes: DocumentRequestNote[];
    documents: DocumentRequestDocument[];
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.DocumentRequestDocument
 *
 * @export
 * @interface DocumentRequestDocument
 */
export interface DocumentRequestDocument {
    documentRequestId: number;
    documentNumber: number;
    documentId: number;
    createdAt: CSharpDateTime;
    createdByUserGuid: string;
}

/**
 * @see Economic.EntryStatus
 *
 * @export
 * @enum {number}
 */
export enum EntryStatusEnum {
    None = 0,
    Booked = 1,
    NonBooked = 2,
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.CreateNoteModel
 *
 * @export
 * @interface CreateNoteModel
 */
export interface CreateNoteModel {
    /**
     * @remarks
     * [Required]: RequiredField
     * [MaxLength]: The field Text must be a string or array type with a maximum length of '210'.
     * @type string
     * @memberof CreateNoteModel
     */
    text: string;
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.CreateDocumentRequestsModel
 *
 * @export
 * @interface CreateDocumentRequestsModel
 */
export interface CreateDocumentRequestsModel {
    note: string;
    draftEntries: DraftEntryIdentifierModel[];
}

/**
 * @see Economic.InternalApi.Model.DocumentRequest.ApproveDocumentRequestModel
 *
 * @export
 * @interface ApproveDocumentRequestModel
 */
export interface ApproveDocumentRequestModel {
    documentNumber: number;
}

/**
 * @see Economic.InternalApi.Model.FeatureFeedback.NewFeatureFeedback
 *
 * @export
 * @interface NewFeatureFeedback
 */
export interface NewFeatureFeedback {
    featureName: string;
    feedback: string;
    phoneNumber: string;
}

/**
 * @see Economic.InternalApi.Model.FeatureFeedback.FeatureFeedback
 *
 * @export
 * @interface FeatureFeedback
 */
export interface FeatureFeedback {
    id: number;
    /**
     * @remarks
     * [TranslationKey]
     * @type number
     * @memberof FeatureFeedback
     */
    agreementNumber: number;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof FeatureFeedback
     */
    featureName: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof FeatureFeedback
     */
    userId: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof FeatureFeedback
     */
    feedback: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof FeatureFeedback
     */
    phoneNumber: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof FeatureFeedback
     */
    userRole: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type CSharpDateTime
     * @memberof FeatureFeedback
     */
    creationDateTime: CSharpDateTime;
}

/**
 * @see Economic.InternalApi.Model.FeatureFeedback.DeleteFeatureFeedbackRecords
 *
 * @export
 * @interface DeleteFeatureFeedbackRecords
 */
export interface DeleteFeatureFeedbackRecords {
    ids: number[];
}

/**
 * @see Economic.InternalApi.Model.FeatureSwitcher.UserToggledFeatureUpdateState
 *
 * @export
 * @interface UserToggledFeatureUpdateState
 */
export interface UserToggledFeatureUpdateState {
    feature: UserToggledFeatureEnum;
}

/**
 * @see Economic.Constants.UserToggledFeature
 *
 * @export
 * @enum {number}
 */
export enum UserToggledFeatureEnum {
    PlaceholderFeature = 0,
}

/**
 * @see Economic.InternalApi.Model.FeatureSwitcher.UserToggledFeatureState
 *
 * @export
 * @interface UserToggledFeatureState
 */
export interface UserToggledFeatureState {
    feature: UserToggledFeatureEnum;
    description: string;
    isEnabled: boolean;
}

/**
 * @see Economic.InternalApi.Model.FeatureSwitcher.UserToggledFeatureFeedbackModel
 *
 * @export
 * @interface UserToggledFeatureFeedbackModel
 */
export interface UserToggledFeatureFeedbackModel {
    id: number;
    /**
     * @remarks
     * [TranslationKey]
     * @type number
     * @memberof UserToggledFeatureFeedbackModel
     */
    agreementNumber: number;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof UserToggledFeatureFeedbackModel
     */
    feature: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof UserToggledFeatureFeedbackModel
     */
    featureDescription: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof UserToggledFeatureFeedbackModel
     */
    userId: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof UserToggledFeatureFeedbackModel
     */
    feedback: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type CSharpDateTime
     * @memberof UserToggledFeatureFeedbackModel
     */
    creationDateTime: CSharpDateTime;
}

/**
 * @see Economic.InternalApi.Model.FeatureSwitcher.NewUserToggledFeatureFeedback
 *
 * @export
 * @interface NewUserToggledFeatureFeedback
 */
export interface NewUserToggledFeatureFeedback {
    feature: UserToggledFeatureEnum;
    feedback: string;
}

/**
 * @see Economic.InternalApi.Model.FeatureSwitcher.DeleteUserToggledFeatureFeedbackRecords
 *
 * @export
 * @interface DeleteUserToggledFeatureFeedbackRecords
 */
export interface DeleteUserToggledFeatureFeedbackRecords {
    ids: number[];
}

/**
 * @see Economic.InternalApi.Model.AdminCentral.UnreconciledSummaryDetails
 *
 * @export
 * @interface UnreconciledSummaryDetails
 */
export interface UnreconciledSummaryDetails {
    account: number;
    accountName: string;
    unreconciledEntries: number;
}

/**
 * @see Economic.InternalApi.Model.AdminCentral.DocRequestSummary
 *
 * @export
 * @interface DocRequestSummary
 */
export interface DocRequestSummary {
    status: number;
    quantity: number;
    entryStatus: EntryStatusEnum;
}

/**
 * @see Economic.InternalApi.Model.AdminCentral.JournalDraftEntryTotal
 *
 * @export
 * @interface JournalDraftEntryTotal
 */
export interface JournalDraftEntryTotal {
    daybookNumber: number;
    name: string;
    draftTotal: number;
}

/**
 * @see Economic.InternalApi.Model.AdminCentral.CreditroInfo
 *
 * @export
 * @interface CreditroInfo
 */
export interface CreditroInfo {
    creditroKYCStatus: CreditroKYCStatus;
    creditroClientGuid: string;
}

/**
 * @see Economic.InternalApi.Model.AdminCentral.CompanyData
 *
 * @export
 * @interface CompanyData
 */
export interface CompanyData {
    agreementNumber: number;
    inboxDocuments?: number;
    journals: JournalDraftEntryTotal[];
    bankReconciliation: UnreconciledSummaryDetails[];
    creditroInfo: CreditroInfo | null;
    docRequests: DocRequestSummary[];
}

/**
 * @see Economic.InternalApi.Model.AdminCentral.DataPoint.BaseDataPoint
 *
 * @export
 * @interface BaseDataPoint
 */
export interface BaseDataPoint {
    agreementNumber: number;
}

/**
 * @see Economic.InternalApi.Model.AdminCentral.DataPoint.JournalDataPoint
 *
 * @export
 * @interface JournalDataPoint
 */
export interface JournalDataPoint extends BaseDataPoint {
    journals: JournalDraftEntryTotal[];
}

/**
 * @see Economic.InternalApi.Model.AdminCentral.DataPoint.InboxDataPoint
 *
 * @export
 * @interface InboxDataPoint
 */
export interface InboxDataPoint extends BaseDataPoint {
    inboxDocuments: number | null;
}

/**
 * @see Economic.InternalApi.Model.AdminCentral.DataPoint.BankReconcilidationDataPoint
 *
 * @export
 * @interface BankReconcilidationDataPoint
 */
export interface BankReconcilidationDataPoint extends BaseDataPoint {
    bankReconciliation: UnreconciledSummaryDetails[];
}

/**
 * @see Economic.InternalApi.Model.AdminCentral.DataPoint.CreditroDataPoint
 *
 * @export
 * @interface CreditroDataPoint
 */
export interface CreditroDataPoint extends BaseDataPoint {
    creditroInfo: CreditroInfo;
}

/**
 * @see Economic.InternalApi.Model.AdminCentral.DataPoint.DocRequestDataPoint
 *
 * @export
 * @interface DocRequestDataPoint
 */
export interface DocRequestDataPoint extends BaseDataPoint {
    docRequests: DocRequestSummary[];
}

/**
 * @see Economic.InternalApi.Model.AdminCentral.DataPoint.AiiaDataPoint
 *
 * @export
 * @interface AiiaDataPoint
 */
export interface AiiaDataPoint extends BaseDataPoint {
    aiiaFlags: boolean[];
}

/**
 * @see Economic.InternalApi.Model.AdminCentral.InboxData
 *
 * @export
 * @interface InboxData
 */
export interface InboxData {
    Total: number;
    inboxCounts: InboxCount[];
}

/**
 * @see Economic.InternalApi.Model.AdminCentral.InboxCount
 *
 * @export
 * @interface InboxCount
 */
export interface InboxCount {
    name: string;
    inboxGroup: InboxGroup;
    count: number | undefined;
}

/**
 * @see Economic.Constants.EconomicEnum
 *
 * @export
 * @enum {number}
 */
export enum InboxGroup {
    none = 0,
    documents = 1,
    categories = 2,
}

/**
 * @see Economic.InternalApi.Model.Inbox.InboxDocumentPages
 *
 * @export
 * @interface InboxDocumentPages
 */
export interface InboxDocumentPages {
    id: number;
    documentNumber: number;
    pages: DocumentPage[];
}

/**
 * @see Economic.InternalApi.Model.Inbox.DocumentPage
 *
 * @export
 * @interface DocumentPage
 */
export interface DocumentPage {
    id: number;
    documentNumber: number;
    pageNumber: number;
    imageUrl: string;
    thumbnailUrl: string;
    imageDataKey: string;
    imageStorageId: number;
}

/**
 * @see Economic.InternalApi.Model.Inbox.DocumentFetchPages
 *
 * @export
 * @enum {number}
 */
export enum DocumentFetchPagesEnum {
    None = 0,
    All = 1,
    First = 2,
}

/**
 * @see Economic.InternalApi.Model.InternalApi.JournalFormFields
 *
 * @export
 * @interface JournalFormFields
 */
export interface JournalFormFields {
    journalId: number;
    entryFormFields: EntryFormField[][];
}

/**
 * @see Economic.InternalApi.Model.InternalApi.EntryFormField
 *
 * @export
 * @interface EntryFormField
 */
export interface EntryFormField {
    size: number;
    name: string;
    skip?: boolean;
}

/**
 * @see Economic.InternalApi.Model.Inventorying.PurchaseOrders.PurchaseOrderResendEmailOptionsResponse
 *
 * @export
 * @interface PurchaseOrderResendEmailOptionsResponse
 */
export interface PurchaseOrderResendEmailOptionsResponse {
    logId: number;
    purchaseOrderId: number;
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
}

/**
 * @see Economic.InternalApi.Model.Inventorying.PurchaseOrders.PurchaseOrderResendEmailOptionsRequest
 *
 * @export
 * @interface PurchaseOrderResendEmailOptionsRequest
 */
export interface PurchaseOrderResendEmailOptionsRequest {
    logId: number;
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
}

/**
 * @see Economic.InternalApi.Model.Inventory.PurchaseOrders.SendPurchaseOrderResponse
 *
 * @export
 * @interface SendPurchaseOrderResponse
 */
export interface SendPurchaseOrderResponse {
    pdfLink: string;
}

/**
 * @see Economic.InternalApi.Model.Inventory.PurchaseOrders.SendPurchaseOrderRequest
 *
 * @export
 * @interface SendPurchaseOrderRequest
 */
export interface SendPurchaseOrderRequest {
    purchaseOrderId: number;
    sendEmail: boolean;
    subject: string;
    saveSubject: boolean;
    body: string;
    saveBody: boolean;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    showAfterSending: boolean;
}

/**
 * @see Economic.InternalApi.Model.Inventory.PurchaseOrders.SendPurchaseOrderInformation
 *
 * @export
 * @interface SendPurchaseOrderInformation
 */
export interface SendPurchaseOrderInformation {
    isSent: boolean;
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    showAfterSending: boolean;
}

/**
 * @see Economic.InternalApi.Model.Inventory.Location
 *
 * @export
 * @interface Location
 */
export interface Location {
    /**
     * @remarks
     * [GreaterThanZero]: RequiredField
     * @type number
     * @memberof Location
     */
    number: number;
    /**
     * @remarks
     * [Required]: RequiredField
     * @type string
     * @memberof Location
     */
    name: string;
    costPriceMarkupPct?: number;
}

export interface DeliveryLocation {
    id: number;
    userId: number;
    address: string;
    postCode: string;
    city: string;
    country: string;
    deliveryTerms: string;
    externalId: string;
    access: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.SendInvoiceEmailFromArchiveRequest
 *
 * @export
 * @interface SendInvoiceEmailFromArchiveRequest
 */
export interface SendInvoiceEmailFromArchiveRequest {
    invoiceId: number;
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    includeOioLink: boolean;
    includeOioAsAttachment: boolean;
    saveSubject: boolean;
    saveBody: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.SendEmailFromArchiveInformationRequest
 *
 * @export
 * @interface SendEmailFromArchiveInformationRequest
 */
export interface SendEmailFromArchiveInformationRequest {
    invoiceId: number;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.SendInvoiceEmailFromArchiveInformationResponse
 *
 * @export
 * @interface SendInvoiceEmailFromArchiveInformationResponse
 */
export interface SendInvoiceEmailFromArchiveInformationResponse {
    invoiceId: number;
    isCreditNote: boolean;
    canBeSentElectronically: boolean;
    hasBeenSentElectronically: boolean;
    defaultDisableEInvoicing: boolean;
    emailOptions: SendInvoiceEmailFromArchiveEmailOptionsResponse;
    validCustomerContacts: CustomerContact[];
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.SendInvoiceEmailFromArchiveEmailOptionsResponse
 *
 * @export
 * @interface SendInvoiceEmailFromArchiveEmailOptionsResponse
 */
export interface SendInvoiceEmailFromArchiveEmailOptionsResponse {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    includeOioLink: boolean;
    includeOioAsAttachment: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.SendInvoiceElectronicallyFromArchiveResponse
 *
 * @export
 * @interface SendInvoiceElectronicallyFromArchiveResponse
 */
export interface SendInvoiceElectronicallyFromArchiveResponse {
    oioId: number;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.SendInvoiceElectronicallyFromArchiveRequest
 *
 * @export
 * @interface SendInvoiceElectronicallyFromArchiveRequest
 */
export interface SendInvoiceElectronicallyFromArchiveRequest {
    invoiceId: number;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.ResendMultipleInvoicesEmailRequest
 *
 * @export
 * @interface ResendMultipleInvoicesEmailRequest
 */
export interface ResendMultipleInvoicesEmailRequest {
    logId: number;
    emailOptions: ResendMultipleInvoicesEmailOptions;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.ResendMultipleInvoicesEmailOptions
 *
 * @export
 * @interface ResendMultipleInvoicesEmailOptions
 */
export interface ResendMultipleInvoicesEmailOptions {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.ResendMultipleInvoicesEmailInformationResponse
 *
 * @export
 * @interface ResendMultipleInvoicesEmailInformationResponse
 */
export interface ResendMultipleInvoicesEmailInformationResponse {
    exceededSendingLimit: boolean;
    emailOptions: ResendMultipleInvoicesEmailOptions;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.ResendInvoiceInformationResponse
 *
 * @export
 * @interface ResendInvoiceInformationResponse
 */
export interface ResendInvoiceInformationResponse {
    invoiceId: number;
    isCreditNote: boolean;
    canBeSentElectronically: boolean;
    hasBeenSentElectronically: boolean;
    emailOptions: ResendInvoiceInformationEmailOptions;
    eInvoiceOptions: ResendInvoiceInformationEInvoiceOptions;
    exceededSendingLimit: boolean;
    validCustomerContacts: CustomerContact[];
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.ResendInvoiceInformationEmailOptions
 *
 * @export
 * @interface ResendInvoiceInformationEmailOptions
 */
export interface ResendInvoiceInformationEmailOptions {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    includeOioLink: boolean;
    includeOioAsAttachment: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.ResendInvoiceInformationEinvoiceOptions
 *
 * @export
 * @interface ResendInvoiceInformationEInvoiceOptions
 */
export interface ResendInvoiceInformationEInvoiceOptions {
    sendingMethod: number;
    cvr: string;
    gln: string;
    pNumber: string;
    peppolId?: string;
    yourRef?: number;
    attentionRef?: number;
    customerNumber: number;
    customerCountry?: string;
    canSendViaPeppol: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.ResendInvoiceElectronicallyRequest
 *
 * @export
 * @interface ResendInvoiceElectronicallyRequest
 */
export interface ResendInvoiceElectronicallyRequest {
    invoiceId: number;
    eInvoiceOptions?: SendInvoiceElectronicallyOptions;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.SendInvoiceElectronicallyOptions
 *
 * @export
 * @interface SendInvoiceElectronicallyOptions
 */
export interface SendInvoiceElectronicallyOptions {
    sendingMethod: number;
    gln?: number;
    cvr?: number;
    pNumber?: number;
    yourRef?: number;
    attentionRef?: number;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.ResendEmailInformationRequest
 *
 * @export
 * @interface ResendEmailInformationRequest
 */
export interface ResendEmailInformationRequest {
    logId: number;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.ResendElectronicallyInformationRequest
 *
 * @export
 * @interface ResendElectronicallyInformationRequest
 */
export interface ResendElectronicallyInformationRequest {
    oioId: number;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.InvoiceResendEmailOptionsRequest
 *
 * @export
 * @interface InvoiceResendEmailOptionsRequest
 */
export interface InvoiceResendEmailOptionsRequest {
    invoiceId: number;
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    includeOioLink: boolean;
    includeOioAsAttachment: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.InvoiceBookAndSendResponse
 *
 * @export
 * @interface InvoiceBookAndSendResponse
 */
export interface InvoiceBookAndSendResponse {
    documentId: number;
    pdfLink: string;
    messages: InvoicingMessage[];
    electronicInvoiceCopyRecipients: string[];
}

/**
 * @see Economic.InternalApi.Model.Invoicing.InvoicingMessage
 *
 * @export
 * @interface InvoicingMessage
 */
export interface InvoicingMessage {
    type: number;
    message: string;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.InvoiceBookAndSendRequest
 *
 * @export
 * @interface InvoiceBookAndSendRequest
 */
export interface InvoiceBookAndSendRequest {
    sendByType: SendByTypeEnum;
    invoiceDraftId: number;
    showPdf: boolean;
    includePaymentLink: boolean;
    emailOptions: InvoiceBookAndSendEmailOptionsRequest;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.InvoiceBookAndSendEmailOptionsRequest
 *
 * @export
 * @interface InvoiceBookAndSendEmailOptionsRequest
 */
export interface InvoiceBookAndSendEmailOptionsRequest {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    saveSubject: boolean;
    saveBody: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    includeOioLink: boolean;
    includeOioAsAttachment: boolean;
    encrypt: boolean;
}

/**
 * @see Economic.Constants.SendByType
 *
 * @export
 * @enum {number}
 */
export enum SendByTypeEnum {
    undefined = 0,
    oioxml = 1,
    email = 2,
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.BulkSendFromArchiveResponse
 *
 * @export
 * @interface BulkSendFromArchiveResponse
 */
export interface BulkSendFromArchiveResponse {
    sentInvoices: number[];
    failedToSendCount: number;
    messages: InvoicingMessage[];
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.BulkSendFromArchiveRequest
 *
 * @export
 * @interface BulkSendFromArchiveRequest
 */
export interface BulkSendFromArchiveRequest {
    sendByType: SendByTypeEnum;
    documentIds: number[];
    subject: string;
    body: string;
    saveSubject: boolean;
    saveBody: boolean;
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    includeOioLink: boolean;
    includeOioAsAttachment: boolean;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.BulkSendFromArchiveInformationResponse
 *
 * @export
 * @interface BulkSendFromArchiveInformationResponse
 */
export interface BulkSendFromArchiveInformationResponse {
    subject: string;
    body: string;
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    includeOioLink: boolean;
    includeOioAsAttachment: boolean;
    sendElectronicInvoicesInfo: InvoiceInfo[];
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.BulkSendFromArchiveInformationRequest
 *
 * @export
 * @interface BulkSendFromArchiveInformationRequest
 */
export interface BulkSendFromArchiveInformationRequest {
    invoiceIds: number[];
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.BookBulkInvoicesResponse
 *
 * @export
 * @interface BookBulkInvoicesResponse
 */
export interface BookBulkInvoicesResponse {
    pdfLink: string;
    failedToBookCount: number;
    failedToSendCount: number;
    bookedInvoices: number[];
    messages: InvoicingMessage[];
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.BookBulkInvoicesRequest
 *
 * @export
 * @interface BookBulkInvoicesRequest
 */
export interface BookBulkInvoicesRequest {
    bookOnly: boolean;
    newInvoiceDate?: CSharpDateTime;
    showInvoicesAfterBooking: boolean;
    invoicesToSendElectronically: number[];
    invoicesToSendByEmail: number[];
    includePaymentLink: boolean;
    emailOptions: InvoiceBookAndSendEmailOptionsRequest;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.BookAllInvoicesResponse
 *
 * @export
 * @interface BookAllInvoicesResponse
 */
export interface BookAllInvoicesResponse {
    booked: number;
    failed: number;
    left: number;
    bookAfterId?: number;
    messages: DraftInvoiceErrorCollection[];
    complete: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.DraftInvoiceErrorCollection
 *
 * @export
 * @interface DraftInvoiceErrorCollection
 */
export interface DraftInvoiceErrorCollection {
    invoiceDraftId: number;
    messages: string[];
    displayNumber: number;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Invoices.BookAllInvoicesRequest
 *
 * @export
 * @interface BookAllInvoicesRequest
 */
export interface BookAllInvoicesRequest {
    bookAfterId?: number;
    batchSize: number;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Orders.OrderSendResult
 *
 * @export
 * @interface OrderSendResult
 */
export interface OrderSendResult {
    pdfLink: string;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Orders.OrderSendRequest
 *
 * @export
 * @interface OrderSendRequest
 */
export interface OrderSendRequest {
    sendByType: SendByTypeEnum;
    orderId: number;
    showPdf: boolean;
    emailOptions: OrderSendEmailOptionsRequest;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Orders.OrderSendEmailOptionsRequest
 *
 * @export
 * @interface OrderSendEmailOptionsRequest
 */
export interface OrderSendEmailOptionsRequest {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    saveSubject: boolean;
    saveBody: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Orders.OrderSendEmailOptionsResult
 *
 * @export
 * @interface OrderSendEmailOptionsResult
 */
export interface OrderSendEmailOptionsResult {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    showPdf: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Orders.OrderResendEmailRequest
 *
 * @export
 * @interface OrderResendEmailRequest
 */
export interface OrderResendEmailRequest {
    logId: number;
    emailOptions: OrderResendEmailOptions;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Orders.OrderResendEmailOptions
 *
 * @export
 * @interface OrderResendEmailOptions
 */
export interface OrderResendEmailOptions {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Orders.OrderResendEmailInformationResponse
 *
 * @export
 * @interface OrderResendEmailInformationResponse
 */
export interface OrderResendEmailInformationResponse {
    logId: number;
    orderId: number;
    exceededSendingLimit: boolean;
    emailOptions: OrderResendEmailInformationEmailOptions;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Orders.OrderResendEmailInformationEmailOptions
 *
 * @export
 * @interface OrderResendEmailInformationEmailOptions
 */
export interface OrderResendEmailInformationEmailOptions {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Orders.GetInformationOrderResult
 *
 * @export
 * @interface GetInformationOrderResult
 */
export interface GetInformationOrderResult {
    emailOptions: OrderSendEmailOptionsResult;
    messages: InvoicingMessage[];
    exceededSendingLimit: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Orders.GetInformationOrderRequest
 *
 * @export
 * @interface GetInformationOrderRequest
 */
export interface GetInformationOrderRequest {
    orderId: number;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Quotes.QuoteSendResult
 *
 * @export
 * @interface QuoteSendResult
 */
export interface QuoteSendResult {
    pdfLink: string;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Quotes.QuoteSendRequest
 *
 * @export
 * @interface QuoteSendRequest
 */
export interface QuoteSendRequest {
    sendByType: SendByTypeEnum;
    quoteId: number;
    showPdf: boolean;
    emailOptions: QuoteSendEmailOptionsRequest;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Quotes.QuoteSendEmailOptionsRequest
 *
 * @export
 * @interface QuoteSendEmailOptionsRequest
 */
export interface QuoteSendEmailOptionsRequest {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    saveSubject: boolean;
    saveBody: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Quotes.QuoteSendEmailOptionsResult
 *
 * @export
 * @interface QuoteSendEmailOptionsResult
 */
export interface QuoteSendEmailOptionsResult {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    showPdf: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Quotes.QuoteResendEmailInformationResponse
 *
 * @export
 * @interface QuoteResendEmailInformationResponse
 */
export interface QuoteResendEmailInformationResponse {
    logId: number;
    quoteId: number;
    emailOptions: QuoteResendEmailInformationEmailOptions;
    exceededSendingLimit: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Quotes.QuoteResendEmailInformationEmailOptions
 *
 * @export
 * @interface QuoteResendEmailInformationEmailOptions
 */
export interface QuoteResendEmailInformationEmailOptions {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Quotes.QuoteResendEmailInformationRequest
 *
 * @export
 * @interface QuoteResendEmailInformationRequest
 */
export interface QuoteResendEmailInformationRequest {
    logId: number;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Quotes.QuoteResendEmailRequest
 *
 * @export
 * @interface QuoteResendEmailRequest
 */
export interface QuoteResendEmailRequest {
    logId: number;
    emailOptions: QuoteResendEmailOptions;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Quotes.QuoteResendEmailOptions
 *
 * @export
 * @interface QuoteResendEmailOptions
 */
export interface QuoteResendEmailOptions {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Quotes.GetInformationQuoteResult
 *
 * @export
 * @interface GetInformationQuoteResult
 */
export interface GetInformationQuoteResult {
    emailOptions: QuoteSendEmailOptionsResult;
    messages: InvoicingMessage[];
    exceededSendingLimit: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Quotes.GetInformationQuoteRequest
 *
 * @export
 * @interface GetInformationQuoteRequest
 */
export interface GetInformationQuoteRequest {
    quoteId: number;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Reminders.ReminderFilter
 *
 * @export
 * @interface ReminderFilter
 */
export interface ReminderFilter {
    entryNumber?: number;
    fromCustomerGroup: number;
    toCustomerGroup: number;
    fromCustomer: number;
    toCustomer: number;
    fromDate?: CSharpDateTime;
    toDate: CSharpDateTime;
    currency: string;
    exchangeRate: number;
    reminderLevel: ReminderLevelEnum;
    gracePeriod: number;
    reminderFee: number;
    interestCalculationMethod: number;
    interestRate: number;
    trifleLimit: number;
    totalTrifle: number;
    openingBalanceText: string;
    invoiceEntryText: string;
    paymentTerm: number;
    paymentDate: CSharpDateTime;
    headline: string;
    text: string;
    interestText: string;
    feeText: string;
    economicBarred: number;
    economicFeesOnly: number;
}

/**
 * @see Economic.Dto.Likvido.LikvidoReminderStatus
 *
 * @export
 * @enum LikvidoReminderStatus
 */
export enum LikvidoReminderStatus {
    NotEligibleForOnboarding = 1,
    OnboardingRequired = 2,
    AlreadyOnboarded = 3,
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Reminders.GenerateReminderResult
 *
 * @export
 * @enum LikvidoReminderStatus
 */
export interface GenerateReminderResult {
    draftId: number;
    likvidoReminderStatus: LikvidoReminderStatus;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Reminders.ReminderFormState
 *
 * @export
 * @interface ReminderFormState
 */
export interface ReminderFormState {
    headline: string;
    text: string;
    interestText: string;
    feeText: string;
    trifleLimit: number;
    totalTrifle: number;
    gracePeriod: number;
    interestRate: number;
    reminderFee: number;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Reminders.ReminderResendEmailOptionsResponse
 *
 * @export
 * @interface ReminderResendEmailOptionsResponse
 */
export interface ReminderResendEmailOptionsResponse {
    logId: number;
    reminderId: number;
    reminderLevel: ReminderLevelEnum;
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    exceededSendingLimit: boolean;
}

/**
 * @see Economic.ReminderLevel
 *
 * @export
 * @enum {number}
 */
export enum ReminderLevelEnum {
    // Used for likvido filtering and no reminders can actually be created with this type
    None = -1,
    StatementOfAccount = 0,
    Reminder1 = 1,
    Reminder2 = 2,
    Reminder3 = 3,
    // Used by reminder domain to create a draft reminder that cannot be booked & sent
    DebtCollection = 4,
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Reminders.ReminderResendEmailOptionsRequest
 *
 * @export
 * @interface ReminderResendEmailOptionsRequest
 */
export interface ReminderResendEmailOptionsRequest {
    logId: number;
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Reminders.ReminderBookingResult
 *
 * @export
 * @interface ReminderBookingResult
 */
export interface ReminderBookingResult {
    bookedReminders: number[];
    validationErrors: string[];
    skippedCustomers: string[];
    pdfLink: string;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Reminders.GetReminderEmailOptionsResponse
 *
 * @export
 * @interface GetReminderEmailOptionsResponse
 */
export interface GetReminderEmailOptionsResponse {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    showPdf: boolean;
    exceededSendingLimit: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Reminders.GetBulkReminderEmailOptionsResponse
 *
 * @export
 * @interface GetBulkReminderEmailOptionsResponse
 */
export interface GetBulkReminderEmailOptionsResponse {
    subject: string;
    body: string;
    encrypt: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    showPdf: boolean;
    exceededSendingLimit: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Reminders.ReminderRecipients
 *
 * @export
 * @interface ReminderRecipients
 */
export interface ReminderRecipients {
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Reminders.BookAndSendSingleReminderRequest
 *
 * @export
 * @interface BookAndSendSingleReminderRequest
 */
export interface BookAndSendSingleReminderRequest {
    draftNumber: number;
    customerId: number;
    sendEmail: boolean;
    showAfterSending: boolean;
    subject: string;
    body: string;
    saveSubject: boolean;
    saveBody: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    encrypt: boolean;
    recipients: ReminderRecipients;
    reminderFilter?: ReminderFilter;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Reminders.BulkBookAndSendReminderRequest
 *
 * @export
 * @interface BulkBookAndSendReminderRequest
 */
export interface BulkBookAndSendReminderRequest {
    customerIds: number[];
    draftNumber: number;
    sendEmail: boolean;
    showAfterSending: boolean;
    subject: string;
    body: string;
    saveSubject: boolean;
    saveBody: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    encrypt: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Reminders.BatchedReminderBookingInfo
 *
 * @export
 * @interface BatchedReminderBookingInfo
 */
export interface BatchedReminderBookingInfo {
    totalCustomersCount: number;
    batchSize: number;
    customerIdBatches: number[][];
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Reminders.ReminderDraftListView
 *
 * @export
 * @interface ReminderDraftListView
 */
export interface ReminderDraftListView {
    rowType: RowTypeEnum;
    id: number;
    customerNo: number;
    customerName: string;
    email: string;
    interestAboveTrifleLimit?: boolean;
    invoiceNo?: number;
    bookedEntryType?: EntryType;
    invoiceDate?: Date;
    dueDate?: Date;
    text?: string;
    amount?: number;
    remainingAmount?: number;
    interestAmount?: number;
    bookedEntryNo?: number;
}

export enum RowTypeEnum {
    CustomerInfo = 0,
    EmptyLineWithRemaining = 1,
    Entry = 2,
    InterestTotal = 3,
    ChargeTotal = 4,
    InterestAndCharge = 5,
    EmptyLine = 6,
}

/**
 * @see Economic.InternalApi.Model.Invoicing.Reminders.ReminderTableSetup
 *
 * @export
 * @interface ReminderTableSetup
 */
export interface ReminderTableSetup {
    reminderLevel: ReminderLevelEnum;
    header: string;
    canSelectLines: boolean;
    canBookReminders: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.SendElectronicInvoiceInfo
 *
 * @export
 * @interface SendElectronicInvoiceInfo
 */
export interface SendElectronicInvoiceInfo {
    draftId: number;
    canSend: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.SalesDocumentCopyInfo
 *
 * @export
 * @interface SalesDocumentCopyInfo
 */
export interface SalesDocumentCopyInfo {
    customerInformationOutdated: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.InvoiceLineSubtotal
 *
 * @export
 * @interface InvoiceLineSubtotal
 */
export interface InvoiceLineSubtotal {
    draftId: number;
    selected: number[];
}

/**
 * @see Economic.InternalApi.Model.Invoicing.BulkDiscountRequest
 *
 * @export
 * @interface BulkDiscountRequest
 */
export interface BulkDiscountRequest {
    draftId: number;
    selectedLineIds: number[];
    discountPercentage: number;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.InvoiceSendEmailOptionsResult
 *
 * @export
 * @interface InvoiceSendEmailOptionsResult
 */
export interface InvoiceSendEmailOptionsResult {
    subject: string;
    body: string;
    recipients: string[];
    recipientsCc: string[];
    recipientsBcc: string[];
    showPdf: boolean;
    includePdfLink: boolean;
    includePdfAsAttachment: boolean;
    includeOioLink: boolean;
    includeOioAsAttachment: boolean;
    encrypt: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.GetInformationBookedInvoiceResult
 *
 * @export
 * @interface GetInformationBookedInvoiceResult
 */
export interface GetInformationBookedInvoiceResult {
    isCreditNote: boolean;
    canSendElectronically: boolean;
    emailOptions: InvoiceSendEmailOptionsResult;
    messages: InvoicingMessage[];
}

/**
 * @see Economic.InternalApi.Model.Invoicing.GetInformationInvoiceResult
 *
 * @export
 * @interface GetInformationInvoiceResult
 */
export interface GetInformationInvoiceResult {
    isCreditNote: boolean;
    canSendElectronically: boolean;
    canIncludePaymentLink: boolean;
    sendByType: SendByTypeEnum;
    emailOptions: InvoiceSendEmailOptionsResult;
    messages: InvoicingMessage[];
}

/**
 * @see Economic.InternalApi.Model.Invoicing.GetInformationInvoiceRequest
 *
 * @export
 * @interface GetInformationInvoiceRequest
 */
export interface GetInformationInvoiceRequest {
    invoiceDraftId: number;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.BookedSalesDocumentCopyInfo
 *
 * @export
 * @interface BookedSalesDocumentCopyInfo
 */
export interface BookedSalesDocumentCopyInfo {
    customerInformationOutdated: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.BulkInvoicesBookingResult
 *
 * @export
 * @interface BulkInvoicesBookingResult
 */
export interface BulkInvoicesBookingResult {
    pdfLink: string;
    bookedInvoices: number[];
    messages: InvoicingMessage[];
}

/**
 * @see Economic.InternalApi.Model.Invoicing.BulkInvoicesBookingRequest
 *
 * @export
 * @interface BulkInvoicesBookingRequest
 */
export interface BulkInvoicesBookingRequest {
    showPdf: boolean;
    encrypt: boolean;
    linkTypePDF: number;
    linkTypeOIOXML: number;
    subject: string;
    body: string;
    sendByType: SendByTypeEnum;
    saveSubject: boolean;
    saveBody: boolean;
    documentIds: number[];
    newFakturaDate?: CSharpDateTime;
    includePaymentLink: boolean;
}

/**
 * @see Economic.InternalApi.Model.Invoicing.BulkInvoicesBookingInfoResult
 *
 * @export
 * @interface BulkInvoicesBookingInfoResult
 */
export interface BulkInvoicesBookingInfoResult {
    subject: string;
    body: string;
    messages: InvoicingMessage[];
    draftInvoiceErrors: DraftInvoiceErrorCollection[];
    checkedShowPdf: boolean;
    checkedEncrypt: boolean;
    checkedEmailLinkPdf: boolean;
    checkedEmailLinkOIOXML: boolean;
    checkedAttachPdf: boolean;
    checkedAttachOIOXML: boolean;
    canIncludePaymentLink: boolean;
    newInvoiceDate: CSharpDateTime;
    sendElectronicInvoicesInfo: SendElectronicInvoiceInfo[];
}

/**
 * @see Economic.InternalApi.Model.Invoicing.BulkInvoicesBookingInfoRequest
 *
 * @export
 * @interface BulkInvoicesBookingInfoRequest
 */
export interface BulkInvoicesBookingInfoRequest {
    invoiceDraftIds: number[];
}

/**
 * @see Economic.InternalApi.Model.Login.LoginUserResponse
 *
 * @export
 * @interface LoginUserResponse
 */
export interface LoginUserResponse {
    accessToken: string;
}

/**
 * @see Economic.InternalApi.Model.Login.LoginUserRequest
 *
 * @export
 * @interface LoginUserRequest
 */
export interface LoginUserRequest {
    agreementNumber: number;
    userId: string;
    password: string;
}

/**
 * @see Economic.InternalApi.Model.Modules.ModuleDiscount
 *
 * @export
 * @interface ModuleDiscount
 */
export interface ModuleDiscount {
    discount: number;
    expiresOn: CSharpDateTime;
    economicUserId: string;
    campaignName: string;
}

/**
 * @see Economic.InternalApi.Model.Modules.AddonUser
 *
 * @export
 * @interface AddonUser
 */
export interface AddonUser {
    economicUserGuid: string;
    permission: string;
    permissions: number;
    agreementNo: number;
}

/**
 * @see Economic.InternalApi.Model.NoteType
 *
 * @export
 * @interface NoteType
 */
export interface NoteType {
    id: number;
    showForAll: boolean;
    name: string;
}

/**
 * @see Economic.InternalApi.Model.Onboarding.OnboardingUpdateCompanyData
 *
 * @export
 * @interface OnboardingUpdateCompanyData
 */
export interface OnboardingUpdateCompanyData {
    name: string;
    address: string;
    zipCode: string;
    city: string;
    phone: string;
    contact: string;
    email: string;
    cvr: string;
    website: string;
}

/**
 * @see Economic.InternalApi.Model.Onboarding.OnboardingUpdateCompanyBankData
 *
 * @export
 * @interface OnboardingUpdateCompanyBankData
 */
export interface OnboardingUpdateCompanyBankData {
    name: string;
    number: string;
    account: string;
    swift: string;
    iban: string;
}

/**
 * @see Economic.InternalApi.Model.Onboarding.OnboardingStatusAccountingYear
 *
 * @export
 * @interface OnboardingStatusAccountingYear
 */
export interface OnboardingStatusAccountingYear {
    from: CSharpDateTime;
    to: CSharpDateTime;
}

/**
 * @see Economic.InternalApi.Model.Onboarding.OnboardingStatusAccountingYears
 *
 * @export
 * @interface OnboardingStatusAccountingYears
 */
export interface OnboardingStatusAccountingYears {
    hasAccountingYears: boolean;
    selectedAccountingYear: OnboardingStatusAccountingYear;
    currentAccountingYear: CSharpDateTime[];
    shiftedAccountingYear: CSharpDateTime[];
}

/**
 * @see Economic.InternalApi.Model.Onboarding.OnboardingStatusCompanyData
 *
 * @export
 * @interface OnboardingStatusCompanyData
 */
export interface OnboardingStatusCompanyData {
    name: string;
    address: string;
    zipCode: string;
    city: string;
    phone: string;
    contact: string;
    email: string;
    cvr: string;
    website: string;
    bankName: string;
    bankRegistrationNumber: string;
    bankAccount: string;
    bankSwift: string;
    bankIban: string;

    isTrial: boolean;
}

/**
 * @see Economic.InternalApi.Model.Onboarding.OnboardingStatusDefaultSetup
 *
 * @export
 * @interface OnboardingStatusDefaultSetup
 */
export interface OnboardingStatusDefaultSetup {
    name: string;
    setup: number;
    isFavorite: boolean;
    hideFromGettingStartedWizard: boolean;
}

/**
 * @see Economic.InternalApi.Model.Onboarding.OnboardingStatusSetups
 *
 * @export
 * @interface OnboardingStatusSetups
 */
export interface OnboardingStatusSetups {
    chosenDefaultSetup: number;
    canApplyDefaultSetup: boolean;
    setups: OnboardingStatusDefaultSetup[];
    errorsPreventingApplyingStandardSetup: string[];
}

/**
 * @see Economic.InternalApi.Model.Onboarding.OnboardingStatus
 *
 * @export
 * @interface OnboardingStatus
 */
export interface OnboardingStatus {
    showGettingStarted: boolean;
    accountActivationLinkSent: boolean;
    setups: OnboardingStatusSetups;
    companyData: OnboardingStatusCompanyData;
    accountingYear: OnboardingStatusAccountingYears;
}

/**
 * @see Economic.InternalApi.Model.Onboarding.OnboardingCreateAccountingYear
 *
 * @export
 * @interface OnboardingCreateAccountingYear
 */
export interface OnboardingCreateAccountingYear {
    from: CSharpDateTime;
    to: CSharpDateTime;
}

/**
 * @see Economic.InternalApi.Model.Onboarding.OnboardingCopyStandardSetup
 *
 * @export
 * @interface OnboardingCopyStandardSetup
 */
export interface OnboardingCopyStandardSetup {
    setupId: number;
    copyLayout: boolean;
}

/**
 * @see Economic.InternalApi.Model.Onboarding.InternalOnboardingStatus
 *
 * @export
 * @interface InternalOnboardingStatus
 */
export interface InternalOnboardingStatus {
    /**
     * A connection to a bank has been established using SmartBank for the agreement number.
     * @type boolean
     * @memberof InternalOnboardingStatus
     */
    bankConnected: boolean;
    /**
     * EAN has been activated for the agreement number.
     * @type boolean
     * @memberof InternalOnboardingStatus
     */
    eanActivated: boolean;
    /**
     * Either one of the agreement’s connected users has accessed the mobile app at any given point.
     * @type boolean
     * @memberof InternalOnboardingStatus
     */
    mobileAppDownloaded: boolean;
    /**
     * When at least one expense has been delivered to the agreement’s Inbox.
     * @type boolean
     * @memberof InternalOnboardingStatus
     */
    expenseSent: boolean;
    /**
     * When the agreement number has automated payment “activated” - i.e. a payment agreement attached in FarPay with payment type “LeverandørService” or “PaymentCard” (Leverandørservice or Payment card activated).
     * @type boolean
     * @memberof InternalOnboardingStatus
     */
    registeredForAutomaticPayment: boolean;
}

/**
 * @see Economic.InternalApi.Model.PaymentLink.PaymentLinkStatus
 *
 * @export
 * @interface PaymentLinkStatus
 */
export interface PaymentLinkStatus {
    apiKey: string;
    loginTab: string;
    merchantStatus: PaymentLinkMerchantStatusEnum;
}

/**
 * @see Economic.PaymentLinkMerchantStatus
 *
 * @export
 * @enum {number}
 */
export enum PaymentLinkMerchantStatusEnum {
    New = 100,
    Processing = 150,
    Active = 200,
    Error = 300,
    Closed = 400,
    Closing = 410,
}

/**
 * @see Economic.InternalApi.Model.PaymentLink.PaymentLinkSetupUpdateRequest
 *
 * @export
 * @interface PaymentLinkSetupUpdateRequest
 */
export interface PaymentLinkSetupUpdateRequest {
    journalId: number;
    paymentAccountId: number;
    feeAccountId: number;
    invoiceLayoutGroupId: number;
    invoiceLayoutGroupAlreadySet: boolean;
}

/**
 * @see Economic.InternalApi.Model.PaymentLink.PaymentLinkSetupResponse
 *
 * @export
 * @interface PaymentLinkSetupResponse
 */
export interface PaymentLinkSetupResponse {
    companyCvr: string;
    companyName: string;
    companyWebsite: string;
    companyDescription: string;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
    companyAddressStreet: string;
    companyAddressStreetNumber: string;
    companyAddressPostCode: string;
    companyAddressCity: string;
    companyAddressCountryCode: string;
    bankName: string;
    bankRegistrationNumber: string;
    bankAccountNumber: string;
    bankIban: string;
    bankSwift: string;
    currency: string;
    estimatedMonthlyTurnover: number;
    merchantStatus?: PaymentLinkMerchantStatusEnum;
    externalOnboardingUrl: string;
    journals: DropDown<number>[];
    journalId: number;
    paymentAccounts: DropDown<number>[];
    paymentAccountId: number;
    feeAccounts: DropDown<number>[];
    feeAccountId: number;
    invoiceLayoutGroups: DropDown<number>[];
    invoiceLayoutGroupId: number;
    invoiceLayoutGroupAlreadySet: boolean;
    /**
     * @remarks
     * [Obsolete]
     * @type boolean
     * @memberof PaymentLinkSetupResponse
     */
    hasSetup: boolean;
    /**
     * @remarks
     * [Obsolete]
     * @type string
     * @memberof PaymentLinkSetupResponse
     */
    kycStatus: string;
}

/**
 * @see Economic.InternalApi.Model.DropDown`1[[System.Int32, mscorlib, Version=4.0.0.0, Culture=neutral, PublicKeyToken=b77a5c561934e089]]
 *
 * @export
 * @interface DropDown<T>
 */
export interface DropDown<T> {
    value: T;
    label: string;
}

/**
 * @see Economic.InternalApi.Model.PaymentLink.PaymentLinkSetupCreateRequest
 *
 * @export
 * @interface PaymentLinkSetupCreateRequest
 */
export interface PaymentLinkSetupCreateRequest {
    companyCvr: string;
    companyName: string;
    companyWebsite: string;
    companyDescription: string;
    companyAddressStreet: string;
    companyAddressStreetNumber: string;
    companyAddressPostCode: string;
    companyAddressCity: string;
    companyAddressCountryCode: string;
    companyEmail: string;
    contactPhone: string;
    contactName: string;
    contactEmail: string;
    bankName: string;
    bankRegistrationNumber: string;
    bankAccountNumber: string;
    bankIban: string;
    bankSwift: string;
    currency: string;
    estimatedMonthlyTurnover: number;
    companyIntegrationKey: string;
    journalId: number;
    feeAccountId: number;
    paymentAccountId: number;
    invoiceLayoutGroupId: number;
}

/**
 * @see Economic.InternalApi.Model.PaymentLink.PaymentLinkSetupCreateRequest
 *
 * @export
 * @interface PaymentLinkSetupCreateRequest
 */
export interface PaymentLinkSetupCreateRequest {
    cvr: string;
    name: string;
    homepage: string;
    description: string;
    contact: string;
    email: string;
    phone: string;
    street: string;
    postCode: string;
    city: string;
    countryCode: string;
    journalId: number;
    feeAccountId: number;
    paymentAccountId: number;
    invoiceLayoutGroupId: number;
    bankName: string;
    bankRegNumber: string;
    bankAccountNumber: string;
    iban: string;
    swift: string;
    currency: string;
    estimatedMonthlyTurnover: number;
}

/**
 * @see Economic.InternalApi.Model.PaymentLink.PaymentLinkSettlement
 *
 * @export
 * @interface PaymentLinkSettlement
 */
export interface PaymentLinkSettlement {
    token: string;
    merchantId: number;
    invoiceId: number;
    amount: number;
    exchangeRate?: number;
    releasedAmount: number;
    feeAmount: number;
    paymentId: string;
    bankDate: CSharpDateTime;
    created: CSharpDateTime;
    checksum: string;
}

/**
 * @see Economic.InternalApi.Model.PaymentLink.PaymentLinkSettingsResponse
 *
 * @export
 * @interface PaymentLinkSettingsResponse
 */
export interface PaymentLinkSettingsResponse {
    customerAccountId: number;
    paymentAccountId: number;
    feeAccountId: number;
}

/**
 * @see Economic.InternalApi.Model.PaymentLink.PaymentLinkSettingsRequest
 *
 * @export
 * @interface PaymentLinkSettingsRequest
 */
export interface PaymentLinkSettingsRequest {
    integrationKey: string;
    invoiceId: number;
}

/**
 * @see Economic.InternalApi.Model.PaymentLink.IntegrationRequestManagementUpdate
 *
 * @export
 * @interface IntegrationRequestManagementUpdate
 */
export interface IntegrationRequestManagementUpdate {
    encoding: number;
    url: string;
    headers: string;
    body: string;
}

/**
 * @see Economic.InternalApi.Model.PaymentLink.IntegrationManagementUpdate
 *
 * @export
 * @interface IntegrationManagementUpdate
 */
export interface IntegrationManagementUpdate {
    providerId: number;
    token: string;
    createMerchant: IntegrationRequestManagementUpdate;
    bankDetails: IntegrationRequestManagementUpdate;
    generateToken: IntegrationRequestManagementUpdate;
    kycStatus: IntegrationRequestManagementUpdate;
}

/**
 * @see Economic.InternalApi.Model.Product.ProductResponse
 *
 * @export
 * @interface ProductResponse
 */
export interface ProductResponse {
    productNumber: string;
    productGroupId: number;
    name: string;
    description: string;
    barCode: string;
    salesPrice?: number;
    costPrice?: number;
    recommendedPrice?: number;
    unitId?: number;
    isAccessible: boolean;
    volume?: number;
    departmentOrDistributionKeyId?: number;
    recommendedCostPrice: number;
    grossWeight?: number;
    netWeight?: number;
    lastUpdated: CSharpDateTime;
    minimumStock?: number;
    minimumOrder?: number;
    defaultLocationId?: number;
    serialNumberPolicy: number;
    unit: string;
    unit2Id?: number;
    environmentalFeeRate?: number;
    packagingRate?: number;
    categoryNumber?: number;
    minimumInventory?: number;
    maximumInventory?: number;
    productDiscountGroupNumber?: number;
    secondSalesPrice?: number;
    isVariantInUse: boolean;
    dimensions: Partial<DimensionData>[];
}

/**
 * @see Economic.InternalApi.Model.Product.ProductRequest
 *
 * @export
 * @interface ProductRequest
 */
export interface ProductRequest {
    /**
     * @remarks
     * [GreaterThanZero]: RequiredField
     * @type string
     * @memberof ProductRequest
     */
    productNumber: string;
    /**
     * @remarks
     * [Required]: RequiredField
     * @type number
     * @memberof ProductRequest
     */
    productGroupId: number;
    /**
     * @remarks
     * [Required]: RequiredField
     * @type string
     * @memberof ProductRequest
     */
    name: string;
    description: string;
    barCode: string;
    /**
     * @remarks
     * [ScalePrecisionValue]: InvalidAmountValue
     * @type number
     * @memberof ProductRequest
     */
    salesPrice?: number;
    /**
     * @remarks
     * [ScalePrecisionValue]: InvalidAmountValue
     * @type number
     * @memberof ProductRequest
     */
    costPrice?: number;
    /**
     * @remarks
     * [ScalePrecisionValue]: InvalidAmountValue
     * @type number
     * @memberof ProductRequest
     */
    recommendedPrice?: number;
    unitId?: number;
    isAccessible: boolean;
    /**
     * @remarks
     * [ScalePrecisionValue]: InvalidAmountValue
     * @type number
     * @memberof ProductRequest
     */
    volume?: number;
    departmentOrDistributionKeyId?: number;
    /**
     * @remarks
     * [Required]: RequiredField
     * [ScalePrecisionValue]: InvalidAmountValue
     * @type number
     * @memberof ProductRequest
     */
    recommendedCostPrice: number;
    /**
     * @remarks
     * [ScalePrecisionValue]: InvalidAmountValue
     * @type number
     * @memberof ProductRequest
     */
    grossWeight?: number;
    /**
     * @remarks
     * [ScalePrecisionValue]: InvalidAmountValue
     * @type number
     * @memberof ProductRequest
     */
    netWeight?: number;
    lastUpdated: CSharpDateTime;
    /**
     * @remarks
     * [ScalePrecisionValue]: InvalidAmountValue
     * @type number
     * @memberof ProductRequest
     */
    minimumStock?: number;
    /**
     * @remarks
     * [ScalePrecisionValue]: InvalidAmountValue
     * @type number
     * @memberof ProductRequest
     */
    minimumOrder?: number;
    defaultLocationId?: number;
    serialNumberPolicy: number;
    unit: string;
    unit2Id?: number;
    /**
     * @remarks
     * [ScalePrecisionValue]: InvalidAmountValue
     * @type number
     * @memberof ProductRequest
     */
    environmentalFeeRate?: number;
    /**
     * @remarks
     * [ScalePrecisionValue]: InvalidAmountValue
     * @type number
     * @memberof ProductRequest
     */
    packagingRate?: number;
    categoryNumber?: number;
    /**
     * @remarks
     * [ScalePrecisionValue]: InvalidAmountValue
     * @type number
     * @memberof ProductRequest
     */
    minimumInventory?: number;
    /**
     * @remarks
     * [ScalePrecisionValue]: InvalidAmountValue
     * @type number
     * @memberof ProductRequest
     */
    maximumInventory?: number;
    productDiscountGroupNumber?: number;
    secondSalesPrice: ProductPriceCurrency;
    dimensions: Partial<DimensionData>[];
}

/**
 * @see Economic.InternalApi.Model.NumberSeries
 *
 * @export
 * @interface NumberSeries
 */
export interface NumberSeries {
    numberSeriesType: NumberSeriesFieldEnum;
    /**
     * @remarks
     * [GreaterThanZero]: E04710
     * @type number
     * @memberof NumberSeries
     */
    nextNumber: number;
}

/**
 * @see Economic.Constants.NumberSeriesField
 *
 * @export
 * @enum {number}
 */
export enum NumberSeriesFieldEnum {
    InvoiceNumber = 0,
    ReminderNumber = 1,
    OrderNumber = 2,
    SupplierInvoiceNumber = 3,
    DeliveryNoteNumber = 4,
    PrepaymentDepreciationsBalanceEntries = 5,
    CorrectiveInvoices = 7,
    VatSettlementEntries = 8,
    UnbrokenNumberSeriesEntries = 9,
}

/**
 * @see Economic.InternalApi.Model.Product.ProductListView
 *
 * @export
 * @interface ProductListView
 */

/**
 * @see Economic.InternalApi.Model.NumberChangePermission
 *
 * @export
 * @interface NumberChangePermission
 */
export interface NumberChangePermission {
    entityId: EntityNumberChangeEnum;
    numberChangeAllowed: boolean;
}

/**
 * @see Economic.Dto.EntityNumberChangeEnum
 *
 * @export
 * @enum {EntityNumberChangeEnum}
 */
export enum EntityNumberChangeEnum {
    Account = 2,
    Creditor = 3,
    Debitor = 4,
    Product = 5,
    Case = 71,
    Subscription = 2110,
}
export interface ProductListView {
    productNumber: string;
    name: string;
    isAccessible: boolean;
    productGroupId: number;
    productGroupName: string;
    salesPrice?: number;
    secondSalesPrice?: number;
    costPrice?: number;
    recommendedCostPrice: number;
    amountInStock?: number;
    amountInOrder?: number;
    amountOrdered?: number;
    amountAvailable?: number;
}

/**
 * @see Economic.InternalApi.Model.PreviousSubscription
 *
 * @export
 * @interface PreviousSubscription
 */
export interface PreviousSubscription {
    number: number;
    name: string;
    startDate: Date;
    endDate: Date;
    terminationDate?: Date;
    invoiceNumber?: number;
    invoiceDate: Date;
    amount?: number;
    isBooked: boolean;
}

/**
 * @see Economic.InternalApi.Model.SubscriptionLine
 *
 * @export
 * @interface SubscriptionLine
 */
export interface SubscriptionLine {
    setupId: number;
    subscriptionId: number;
    lineId: number;
    productId: string;
    description: string;
    quantity?: number;
    specialPrice?: number;
    department?: number;
    dimensionData?: DimensionData[];
}

/**
 * @see Economic.InternalApi.Model.SubscriberListView
 *
 * @export
 * @interface SubscriberListView
 */
export interface SubscriberListView {
    id: number;
    number: number;
    name: string;
    registeredDate: Date;
    startDate: Date;
    endDate: Date;
    expiryDate?: Date;
    discountPercent?: number;
    discountExpiry?: Date;
    specialPrice?: number;
    projectNo?: number;
    priceIndex?: number;
    extraTextForInvoice: string;
}

/**
 * @see Economic.InternalApi.Model.Subscriber
 *
 * @export
 * @interface Subscriber
 */
export interface Subscriber {
    id: number;
    subscriptionId: number;
    customerId: number;
    registeredDate: Date;
    periodStartDate: Date;
    periodEndDate: Date;
    expiryDate?: Date;
    discountPercent?: number;
    discountExpiryDate?: Date;
    specialPrice?: number;
    projectId?: number;
    priceIndex?: number;
    department?: number;
    dimensionData?: DimensionData[];
    comments: string;
    quantityFactor?: number;
    extraTextForInvoice: string;
    yourReferenceId?: number;
    otherReference: string;
}

/**
 * @see Economic.InternalApi.Model.Subscription
 *
 * @export
 * @interface Subscription
 */
export interface Subscription {
    name: string;
    id: number;
    description: string;
    access: boolean;
    accrue: boolean;
    allowMoreThanOnePerCustomer: boolean;
    addPeriod: boolean;
    refund: boolean;
    collection: number;
    calendarYear: boolean;
    intervalType: number;
    includeName: boolean;
}

/**
 * @see Economic.InternalApi.Model.Product.AccountingRules
 *
 * @export
 * @interface AccountingRules
 */
export interface AccountingRules {
    /**
     * @remarks
     * [GreaterThanZero]: InvalidFieldValue
     * @type number
     * @memberof AccountingRules
     */
    domesticAccountNumber: number;
    domesticAccount: string;
    domesticWithoutVatAccountNumber?: number;
    domesticWithoutVatAccount: string;
    euAccountNumber?: number;
    euAccount: string;
    abroadAccountNumber?: number;
    abroadAccount: string;
    currentProjectsAccountNumber?: number;
    currentProjectsAccount: string;
    countrySpecificVatRuleItems?: CountrySpecificVatRuleItem[];
}

/**
 * @see Economic.InternalApi.Model.Product.CountrySpecificVatRuleItem
 *
 * @export
 * @interface CountrySpecificVatRuleItem
 */
export interface CountrySpecificVatRuleItem {
    zoneId?: number;
    accountNumber?: number;
    textId: string;
    name: string;
}

/**
 * @see Economic.InternalApi.Model.Product.ProductGroup
 *
 * @export
 * @interface ProductGroup
 */
export interface ProductGroup {
    /**
     * @remarks
     * [GreaterThanZero]: RequiredField
     * @type number
     * @memberof ProductGroup
     */
    id: number;
    /**
     * @remarks
     * [Required]: RequiredField
     * [StringLength]: ProductGroupNameInvalidLength
     * @type string
     * @memberof ProductGroup
     */
    name: string;
    isServiceProducts: boolean;
    /**
     * @remarks
     * [GreaterThanZero]: InvalidFieldValue
     * @type number
     * @memberof ProductGroup
     */
    inventoryAccountNumber?: number;
    /**
     * @remarks
     * [GreaterThanZero]: InvalidFieldValue
     * @type number
     * @memberof ProductGroup
     */
    costOfGoodsSoldAccountNumber?: number;
    includeInProject: boolean;
    /**
     * @remarks
     * [GreaterThanZero]: InvalidFieldValue
     * @type number
     * @memberof ProductGroup
     */
    accrualAccountNumber?: number;
    accountingRules: AccountingRules;
    hasCountrySpecificVat: boolean;
}

/**
 * @see Economic.InternalApi.Model.Product.ProductDiscountGroup
 *
 * @export
 * @interface ProductDiscountGroup
 */
export interface ProductDiscountGroup {
    /**
     * @remarks
     * [Required]: The Number field is required.
     * [GreaterThanZero]: E04710
     * @type number
     * @memberof ProductDiscountGroup
     */
    number: number;
    /**
     * @remarks
     * [Required]: The Name field is required.
     * @type string
     * @memberof ProductDiscountGroup
     */
    name: string;
}

/**
 * @see Economic.InternalApi.Model.Product.ProductCategory
 *
 * @export
 * @interface ProductCategory
 */
export interface ProductCategory {
    number: number;
    name: string;
    variant1: string;
    variant2: string;
}

/**
 * @see Economic.InternalApi.Model.Product.CreateProductCategory
 *
 * @export
 * @interface CreateProductCategory
 */
export interface CreateProductCategory {
    /**
     * @remarks
     * [Required]: RequiredField
     * @type string
     * @memberof CreateProductCategory
     */
    name: string;
    variant1: string;
    variant2: string;
}

/**
 * @see Economic.InternalApi.Model.Sales.QuoteSums
 *
 * @export
 * @interface QuoteSums
 */
export interface QuoteSums {
    total: number;
    totalSent: number;
    totalNotSent: number;
}

/**
 * @see Economic.InternalApi.Model.Sales.OrderSums
 *
 * @export
 * @interface OrderSums
 */
export interface OrderSums {
    total: number;
    totalSent: number;
    totalNotSent: number;
}

/**
 * @see Economic.InternalApi.Model.Sales.InvoiceSums
 *
 * @export
 * @interface InvoiceSums
 */
export interface InvoiceSums {
    total: number;
}

/**
 * @see Economic.InternalApi.Model.Sales.DashboardUnpaidInvoicesEnvelopeModel
 *
 * @export
 * @interface DashboardUnpaidInvoicesEnvelopeModel
 */
export interface DashboardUnpaidInvoicesEnvelopeModel {
    pageSize: number;
    overduePeriodInDays: number;
    notOverdueTotalAmount: number;
    notOverdueCount: number;
    overdueLessThanOrEqualPeriodTotalAmount: number;
    overdueLessThanOrEqualPeriodCount: number;
    overdueGreaterThanPeriodTotalAmount: number;
    overdueGreaterThanPeriodCount: number;
    unpaidInvoices: DashboardUnpaidInvoiceModel[];
}

/**
 * @see Economic.Dto.Sales.DashboardUnpaidInvoiceOverdueCategory
 *
 * @export
 * @enum {number}
 */
export enum DashboardUnpaidInvoiceOverdueCategoryEnum {
    NotOverdue = 0,
    OverdueLessThanOrEqualPeriod = 1,
    OverdueGreaterThanPeriod = 2,
}

/**
 * @see Economic.InternalApi.Model.Sales.DashboardUnpaidInvoiceModel
 *
 * @export
 * @interface DashboardUnpaidInvoiceModel
 */
export interface DashboardUnpaidInvoiceModel {
    invoiceNr: number;
    customerNr: number;
    customerName: string;
    amountInBaseCurrency: number;
    dueDate?: CSharpDateTime;
    overdueCategory: DashboardUnpaidInvoiceOverdueCategoryEnum;
}

/**
 * @see Economic.InternalApi.Model.Setup.EmailTemplates.UpdateEmailTemplate
 *
 * @export
 * @interface UpdateEmailTemplate
 */
export interface UpdateEmailTemplate {
    id: number;
    documentType: number;
    name: string;
    subject: string;
    body: string;
    isDefault: boolean;
}

/**
 * @see Economic.InternalApi.Model.Setup.EmailTemplates.NewEmailTemplate
 *
 * @export
 * @interface NewEmailTemplate
 */
export interface NewEmailTemplate {
    documentType: number;
    /**
     * @remarks
     * [Required]: The Name field is required.
     * [MaxLength]: The field Name must be a string or array type with a maximum length of '100'.
     * @type string
     * @memberof NewEmailTemplate
     */
    name: string;
    /**
     * @remarks
     * [Required]: The Subject field is required.
     * [MaxLength]: The field Subject must be a string or array type with a maximum length of '500'.
     * @type string
     * @memberof NewEmailTemplate
     */
    subject: string;
    /**
     * @remarks
     * [Required]: The Body field is required.
     * @type string
     * @memberof NewEmailTemplate
     */
    body: string;
    isDefault: boolean;
}

/**
 * @see Economic.InternalApi.Model.Setup.EmailTemplates.EmailTemplateMergeFields
 *
 * @export
 * @interface EmailTemplateMergeFields
 */
export interface EmailTemplateMergeFields {
    name: string;
}

/**
 * @see Economic.InternalApi.Model.Setup.EmailTemplates.EmailTemplate
 *
 * @export
 * @interface EmailTemplate
 */
export interface EmailTemplate {
    id: number;
    documentType: number;
    name: string;
    subject: string;
    body: string;
    isDefault: boolean;
}

export interface ProductCollectionListViewModel {
    id: number;
    name: string;
    salesDocumentId: number;
    totalAmount: number;
}

export interface ProductCollectionModel {
    id: number;
    name: string;
    salesDocumentId: number;
}

export interface ProductCollectionLineListViewModel {
    salesDocumentId: number;
    lineNr: number;
    sortKey: number;
    productId: string;
    productName: string;
    quantity?: number;
    unitNetPrice?: number;
    discountAsPercent?: number;
    total?: number;
    unit: string;
    unit2: string;
    departmentOrDistributionKey: string;
    dimensionData: DimensionData[];
}

export interface ProductCollectionLineModel {
    salesDocumentId: number;
    lineNr: number;
    sortKey: number;
    description: string;
    productId: string;
    quantity?: number;
    quantity2?: number;
    unitNetPrice?: number;
    discountAsPercent?: number;
    UnitId?: number;
    Unit2Id?: number;
    departmentOrDistributionKeyId?: number;
    dimensionData: DimensionData[];
}

export interface ProductCollectionLineBatchDeleteModel {
    lineIds: number[];
}
/**
 * @see Economic.InternalApi.Model.Setup.UpdateCustomerSetup
 *
 * @export
 * @interface UpdateCustomerSetup
 */
export interface UpdateCustomerSetup {
    defaultCustomerGroupId?: number;
    defaultPaymentTermId?: number;
    customerNumberSuggestion: boolean;
    defaultSalesDocumentType: number;
    defaultLayoutGroupNumber?: number;
    isSalesDocumentTypeSelectable: boolean;
    useCustomerAddressForDelivery: boolean;
    defaultInvoiceDeliveryDateType: number;
    defaultInvoiceDeliveryDate?: CSharpDateTime;
    defaultInvoiceDateType: number;
    invoiceLinesIncludeVat: boolean;
    alwaysSendEmailToCustomer: boolean;
    invoiceBookingText: string;
    creditNoteBookingText: string;
    invoiceLinesWithNameAndDescription: number;
    bccAddress: string;
}

/**
 * @see Economic.InternalApi.Model.System.Company
 *
 * @export
 * @interface Company
 */
export interface Company {
    agreeementNumber: number;
    name: string;
    invoiced: boolean;
}

/**
 * @see Economic.InternalApi.Model.Setup.UnitSetup
 *
 * @export
 * @interface UnitSetup
 */
export interface UnitSetup {
    showUnit1: boolean;
    showUnit2: boolean;
    showTaxes: boolean;
    consolidateEntries: boolean;
}

/**
 * @see Economic.InternalApi.Model.Setup.PaymentTerm
 *
 * @export
 * @interface PaymentTerm
 */
export interface PaymentTerm {
    id: number;
    name: string;
    setupId: number;
    typeId: number;
    creditCardCompanyCustomerId?: number;
    description: string;
    numberOfDays?: number;
    percentageForPrepaidAmount?: number;
    percentageForRemainderAmount?: number;
    contraAccountId?: number;
    contraAccount2Id?: number;
    access: boolean;
    useOtherRefAsBookingText: boolean;
    discount?: number;
    discountAccountId?: number;
    discountDay?: number;
}

/**
 * @see Economic.InternalApi.Model.Setup.InvoiceLayoutGroup
 *
 * @export
 * @interface InvoiceLayoutGroup
 */
export interface InvoiceLayoutGroup {
    id: number;
    name: string;
    isHistoric: boolean;
    isLetterHead: boolean;
    defaultFontId?: number;
    defaultUnderline: boolean;
    defaultFontSize?: number;
    defaultColor: string;
    width?: number;
    height?: number;
    thousandSeparator: string;
    decimalSeparator: string;
    specialStatus?: number;
    availableForAgreements: string;
    customerLetterLayoutId?: number;
    supplierLetterLayoutId?: number;
    invoiceLayoutTypeId: number;
}

/**
 * @see Economic.InternalApi.Model.Setup.DepartmentSetup
 *
 * @export
 * @interface DepartmentSetup
 */
export interface DepartmentSetup {
    showDepartment: boolean;
}

/**
 * @see Economic.InternalApi.Model.Setup.DepartmentalDistributionKey
 *
 * @export
 * @interface DepartmentalDistributionKey
 */
export interface DepartmentalDistributionKey {
    distributionKeyNumber: number;
    name: string;
    barred: boolean;
    hasBookedEntries: boolean;
    distributions: DepartmentalDistribution[];
}

/**
 * @see Economic.InternalApi.Model.Setup.DepartmentalDistribution
 *
 * @export
 * @interface DepartmentalDistribution
 */
export interface DepartmentalDistribution {
    percentage: number;
    departmentNumber: number;
}

/**
 * @see Economic.InternalApi.Model.Setup.CustomerSetup
 *
 * @export
 * @interface CustomerSetup
 */
export interface CustomerSetup {
    defaultCustomerGroupId?: number;
    defaultPaymentTermId?: number;
    customerNumberSuggestion: boolean;
    defaultSalesDocumentType: number;
    defaultLayoutGroupNumber?: number;
    isSalesDocumentTypeSelectable: boolean;
    useCustomerAddressForDelivery: boolean;
    defaultInvoiceDeliveryDateType: number;
    defaultInvoiceDeliveryDate?: CSharpDateTime;
    defaultInvoiceDateType: number;
    invoiceLinesIncludeVat: boolean;
    alwaysSendEmailToCustomer: boolean;
    invoiceBookingText: string;
    creditNoteBookingText: string;
    invoiceLinesWithNameAndDescription: number;
    bccAddress: string;
}

/**
 * @see Economic.InternalApi.Model.Wootric.WootricConfigurationModel
 *
 * @export
 * @interface WootricConfigurationModel
 */
export interface WootricConfigurationModel {
    productName: string;
    externalId: string;
    isAdminUser: boolean;
    canParticipateInSurvey: boolean;
    userType: string;
    userEmail: string;
    userSignUpDateUnixEpoch: number;
    agreementSignupDateUnixEpoch: number;
    installedAppsCount: number;
    accountToken: string;
}

/**
 * @see Economic.InternalApi.Model.VismaLinkRequest
 *
 * @export
 * @interface VismaLinkRequest
 */
export interface VismaLinkRequest {
    /**
     * Guid of the user
     * @type string
     * @memberof VismaLinkRequest
     */
    id: string;
    agreementId: number;
    userId: string;
    language: number;
    countryCode: string;
    /**
     * @remarks
     * [JsonConverter]
     * @type string
     * @memberof VismaLinkRequest
     */
    email: string;
    firstName: string;
    lastName: string;
}

/**
 * @see Economic.InternalApi.Model.VismaInviteRequest
 *
 * @export
 * @interface VismaInviteRequest
 */
export interface VismaInviteRequest {
    /**
     * Guid of the user
     * @type string
     * @memberof VismaInviteRequest
     */
    id: string;
    language: number;
    countryCode: string;
    /**
     * @remarks
     * [JsonConverter]
     * @type string
     * @memberof VismaInviteRequest
     */
    email: string;
    firstName: string;
    lastName: string;
}

/**
 * @see Economic.InternalApi.Model.VismaConnectAgreement
 *
 * @export
 * @interface VismaConnectAgreement
 */
export interface VismaConnectAgreement {
    agreementNumber: number;
    companyName: string;
    cvr: string;
    zipCode: string;
    city: string;
    address: string;
    phone: string;
    isAdministrator: boolean;
}

/**
 * @see Economic.InternalApi.Model.VatZone
 *
 * @export
 * @interface VatZone
 */
export interface VatZone {
    number: number;
    id: VatZoneEnum;
    name: string;
    textId: string;
}

/**
 * @see EconomicEnum.VatZone
 *
 * @export
 * @enum {number}
 */
export enum VatZoneEnum {
    Unknown = 0,
    Domestic = 1,
    EU = 2,
    Abroad = 3,
    DomesticNoVat = 4,
    Project = 5,
}
/**
 * @see Economic.InternalApi.Model.UnitModel
 *
 * @export
 * @interface UnitModel
 */
export interface UnitModel {
    unitNo: number;
    /**
     * @remarks
     * [MaxLength]: The field Name must be a string or array type with a maximum length of '100'.
     * @type string
     * @memberof UnitModel
     */
    name: string;
    firstVatAccountId?: number;
    firstVatAccountPercentage?: number;
    secondVatAccountId?: number;
    secondVatAccountPercentage?: number;
}

/**
 * @see Economic.InternalApi.Model.UnitListViewModel
 *
 * @export
 * @interface UnitListViewModel
 */
export interface UnitListViewModel {
    /**
     * @remarks
     * [TranslationKey]
     * @type number
     * @memberof UnitListViewModel
     */
    unitNo: number;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof UnitListViewModel
     */
    name: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type number
     * @memberof UnitListViewModel
     */
    firstVatAccountId?: number;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof UnitListViewModel
     */
    firstVatAccountDisplayNumber: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type number
     * @memberof UnitListViewModel
     */
    firstVatAccountPercentage?: number;
    /**
     * @remarks
     * [TranslationKey]
     * @type number
     * @memberof UnitListViewModel
     */
    secondVatAccountId?: number;
    /**
     * @remarks
     * [TranslationKey]
     * @type string
     * @memberof UnitListViewModel
     */
    secondVatAccountDisplayNumber: string;
    /**
     * @remarks
     * [TranslationKey]
     * @type number
     * @memberof UnitListViewModel
     */
    secondVatAccountPercentage?: number;
}

/**
 * @see Economic.InternalApi.Model.UnitBatchDeleteModel
 *
 * @export
 * @interface UnitBatchDeleteModel
 */
export interface UnitBatchDeleteModel {
    unitIds: number[];
}

/**
 * @see Economic.InternalApi.Model.SupplierSetup
 *
 * @export
 * @interface SupplierSetup
 */
export interface SupplierSetup {
    defaultGroupNumber?: number;
    defaultLayoutGroupNumber?: number;
    automaticNumbering: boolean;
    defaultPaymentTerm?: number;
    supplierDefaultDocumentTypeId: number;
    supplierDocumentTypeStatusOption: boolean;
}

/**
 * @see Economic.Dto.Notes.NoteDto
 *
 * @export
 * @interface NoteDto
 */
export interface NoteDto {
    id: number;
    subject: string;
    authorAgreementNumber: number;
    authorUserId: number;
    agreementNumber: number;
    userId: string;
    completed: boolean;
    createdAt?: Date;
    noteTypeId?: number;
    contactPersonId?: number;
    message: string;
    reminderDate?: Date;
    transferredDate?: Date;
    hasReminder?: boolean;
}

/**
 * @see Economic.Dto.Notes.NoteWithTypeNameDto
 *
 * @export
 * @interface NoteWithTypeNameDto
 */
export interface NoteWithTypeNameDto extends NoteDto {
    noteTypeName: string;
}

/**
 * @see Economic.InternalApi.Model.SupplierGroup
 *
 * @export
 * @interface SupplierGroup
 */
export interface SupplierGroup {
    number: number;
    name: string;
    accountNumber: number;
    hasAccessFromInventory: boolean;
    accounts: SupplierGroupAccounts;
    allocations: SupplierGroupAllocations;
    useDeduction?: boolean;
    deductionPercentage?: number;
    deductionAccountNumber?: number;
    deductionEntryText?: string;
    isDeductionBasedOnItems?: boolean;
    isDeductionBasedOnAmountInclVat?: boolean;
}

/**
 * @see Economic.InternalApi.Model.SupplierGroupAllocations
 *
 * @export
 * @interface SupplierGroupAllocations
 */
export interface SupplierGroupAllocations {
    bulkDiscount?: boolean;
    charge?: boolean;
    freight?: boolean;
}

/**
 * @see Economic.InternalApi.Model.SupplierGroupAccounts
 *
 * @export
 * @interface SupplierGroupAccounts
 */
export interface SupplierGroupAccounts {
    bulkDiscount?: number;
    charge?: number;
    costPriceMarkup?: number;
    freight?: number;
    supplierMarkup?: number;
}

/**
 * @see Economic.InternalApi.Model.Supplier
 *
 * @export
 * @interface Supplier
 */
export interface Supplier {
    number: number;
    name: string;
    cvr: string;
    currency: string;
    address: string;
    postCode: string;
    city: string;
    phone: string;
    isAccessible: boolean;
    email: string;
    country: string;
    vatZone: number;
    layoutGroupId: number;
    attention?: number;
    supplierContact?: number;
    salesPerson?: number;
    bankAccount: string;
    language?: number;
    paymentType?: number;
    paymentID: string;
    defaultInvoiceText: string;
    contraAccount?: Account;
    paymentTerm?: number;
    supplierGroupId: number;
    overrideExtractedPayment: boolean;
    vatCode?: string;
    markupPercentage: number;
    paymentDetails?: PaymentDetails;
}

/**
 * @see Economic.Entity.SupplierLedgerCard
 *
 * @export
 * @interface SupplierLedgerCard
 */
export interface SupplierLedgerCard {
    setupId: number;
    documentNumber?: number;
    booked: string | null;
    paymentType: number;
    date: Date;
    dueDate?: Date;
    entryNumber: number;
    voucherDisplayNumber?: number;
    entryType: number;
    invoiceNumber: string;
    inventoryInvoiceNumber?: number;
    text: string;
    currency: string;
    amountCurrency: number;
    amountDKK: number;
    reminderCurrency: number;
    reminderDKK: number;
    balance: number;
}

/**
 * @see Economic.InternalApi.Model.SubscriptionRetentionResponse
 *
 * @export
 * @interface SubscriptionRetentionResponse
 */
export interface SubscriptionRetentionResponse {
    /**
     * @remarks
     * [Required]: The Response field is required.
     * @type SubscriptionRetentionOptionEnum
     * @memberof SubscriptionRetentionResponse
     */
    response: SubscriptionRetentionOptionEnum;
    timestamp: CSharpDateTime;
}

/**
 * @see Economic.Constants.SubscriptionRetentionOption
 *
 * @export
 * @enum {number}
 */
export enum SubscriptionRetentionOptionEnum {
    Undecided = 0,
    OptIn = 1,
    OptOut = 2,
}

/**
 * @see Economic.InternalApi.Model.PeriodResponse
 *
 * @export
 * @interface PeriodResponse
 */
export interface PeriodResponse {
    accountingYear: string;
    from: CSharpDateTime;
    to: CSharpDateTime;
    number: number;
    periodStatus: PeriodStatusEnum;
}

/**
 * @see EconomicEnum.PeriodStatus
 *
 * @export
 * @enum {number}
 */
export enum PeriodStatusEnum {
    Open = 1,
    Barred = 2,
    Closed = 3,
    BarredAndClosed = 4,
}

/**
 * @see Economic.InternalApi.Model.PaymentTakeoverRequestOverview
 *
 * @export
 * @interface PaymentTakeoverRequestOverview
 */
export interface PaymentTakeoverRequestOverview {
    requesterAdminAgreementNumber: string;
    requesterFullName: string;
    clientCompanyName: string;
    clientAgreementNumber: string;
}

/**
 * @see Economic.InternalApi.Model.PaymentTakeoverRequest
 *
 * @export
 * @interface PaymentTakeoverRequest
 */
export interface PaymentTakeoverRequest {
    guid: string;
    adminAgreementNumber: number;
    adminUserGuid: string;
    clientAgreementNumber: number;
    createDate: CSharpDateTime;
    acceptDate?: CSharpDateTime;
    rejectDate?: CSharpDateTime;
    cancelDate?: CSharpDateTime;
    useDate?: CSharpDateTime;
}

/**
 * @see Economic.InternalApi.Model.ModelBase
 *
 * @export
 * @interface ModelBase
 */
export interface ModelBase {}

/**
 * @see Economic.InternalApi.Model.DocumentMergeRequest
 *
 * @export
 * @interface DocumentMergeRequest
 */
export interface DocumentMergeRequest {
    ids: number[];
    name: string;
}

/**
 * @see Economic.InternalApi.Model.LanguageLayer
 *
 * @export
 * @interface LanguageLayer
 */
export interface LanguageLayer {
    languageLayerNumber: number;
    name: string;
}

/**
 * @see Economic.InternalApi.Model.LanguageLayerProduct
 *
 * @export
 * @interface LanguageLayerProduct
 */
export interface LanguageLayerProduct {
    languageId: number;
    productNumber: string;
    productName: string;
    productDescription: string;
    translatedProductName: string;
    translatedProductDescription: string;
}

/**
 * @see Economic.InternalApi.Model.KeyFigureCode
 *
 * @export
 * @interface KeyFigureCode
 */
export interface KeyFigureCode {
    id: number;
    name: string;
}

/**
 * @see Economic.InternalApi.Model.Journal
 *
 * @export
 * @interface Journal
 */
export interface Journal {
    id: number;
    allowedEntryType: EntryTypeNamesEnum;
    enterBehaviour: boolean;
    balancingBehavior: boolean;
    documentHandlingEnabled: boolean;
    documentHandlingDefaultTransferTo: boolean;
    autoApprove: boolean;
    type: JournalTypeEnum;
    controllingAccount: number;
    navigationListActionId: number;
    isStandingJournal: boolean;
    requireAdminApproval: boolean;
    lockVatCodes: boolean;
    allowVatAmountEditing: boolean;
    warnAboutGapInNumberSeries: boolean;
    warnAboutPossibleSignIssues: boolean;
    nextVoucherNumber?: number;
    minimumVoucherNumber?: number;
    maximumVoucherNumber?: number;
    customerContraAccount?: number;
    supplierContraAccount?: number;
    customerPaymentText: string;
    name: string;
    priority?: number;
    financialContraAccount?: number;
    kvikPopTast?: boolean;
    supplierPaymentText: string;
    numberSeries?: number;
    useInvoiceNoAsPaymentId?: boolean;
    journalBaseType?: number;
    useBalanceAsDefaultAmount?: boolean;
    journalSubType?: number;
    financialPaymentAccount?: number;
    defaultSalesAccount?: number;
    paymentMessageText: string;
    defaultDepartment?: number;
    defaultDimension2?: number;
    defaultDimension3?: number;
    allowPartialBooking: boolean;
    requireBalancePerVoucherId: boolean;
    repeatText: boolean;
    bookingMethod?: number;
    cashAccountNumbers: number[];
}

/**
 * @see Economic.Constants.JournalType
 *
 * @export
 * @enum {number}
 */
export enum JournalTypeEnum {
    Classic = 1,
    JournalPro = 2,
    JournalProTemporaryBooking = 3,
    JournalProSynchronization = 4,
}

/**
 * @see Economic.InternalApi.Model.EmployeeGroup
 *
 * @export
 * @interface EmployeeGroup
 */
export interface EmployeeGroup {
    number: number;
    name: string;
}

/**
 * @see Economic.InternalApi.Model.Employee
 *
 * @export
 * @interface Employee
 */
export interface Employee {
    number: number;
    name: string;
    groupNumber: number;
    access: boolean;
    contact: EmployeeContact;
}

/**
 * @see Economic.InternalApi.Model.EmployeeContact
 *
 * @export
 * @interface EmployeeContact
 */
export interface EmployeeContact {
    telephone: string;
    email: string;
}

/**
 * @see Economic.InternalApi.Model.ElectronicDocumentCompanyRegistrationResponse
 *
 * @export
 * @interface ElectronicDocumentCompanyRegistrationResponse
 */
export interface ElectronicDocumentCompanyRegistrationResponse {
    sproomSubscriptionGln: string;
}

/**
 * @see Economic.InternalApi.Model.ElectronicDocumentCompanyRegistrationRequest
 *
 * @export
 * @interface ElectronicDocumentCompanyRegistrationRequest
 */
export interface ElectronicDocumentCompanyRegistrationRequest {
    registerCvr: boolean;
    registerGln: boolean;
    ignoreDuplicateCvr: boolean;
}

/**
 * @see Economic.InternalApi.Model.DropDown<number>[];
 *
 * @export
 * @interface DropDown<T>
 */
export interface DropDown<T> {
    value: T;
    label: string;
}

/**
 * Validation taken from https://github.com/FluentValidation/FluentValidation/blob/main/src/FluentValidation/Validators/ScalePrecisionValidator.cs
 * @see
 *
 * @export
 * @interface T
 */
export interface T {}

/**
 * @see Economic.InternalApi.Model.Reports.Vat.SkatVatIntegration.SkatVatStatementSubmisssionModel
 *
 * @export
 * @interface SkatVatStatementSubmisssionModel
 */
export interface SkatVatStatementSubmisssionModel {
    id: number;
    vatReportId: number;
    periodFrom: CSharpDateTime;
    periodTo: CSharpDateTime;
    submissionStatus: SkatVatSubmissionStatusEnum;
    skatSubmissionId: CSharpGuid;
    skatApprovalLink: string;
    createdAt: CSharpDateTime;
    lastUpdatedAt?: CSharpDateTime;
}

/**
 * @see Economic.Entity.Reports.Vat.SkatVatIntegration.SkatVatSubmissionStatus
 *
 * @export
 * @enum {number}
 */
export enum SkatVatSubmissionStatusEnum {
    Draft = 0,
    Approved = 1,
}

/**
 * @see Economic.InternalApi.Model.Reports.Vat.SkatVatIntegration.SkatVatIntegrationOnboardingInitiationModel
 *
 * @export
 * @interface SkatVatIntegrationOnboardingInitiationModel
 */
export interface SkatVatIntegrationOnboardingInitiationModel {
    skatOnboardingUrl: string;
}

/**
 * @see Economic.InternalApi.Model.DraftEntryActivationAsset
 *
 * @export
 * @interface DraftEntryActivationAsset
 */
export interface DraftEntryActivationAsset {
    /**
     * @remarks
     * [Required]: The Number field is required.
     * [GreaterThanZero]: E04710
     * @type number
     * @memberof DraftEntryActivationAsset
     */
    number: number;
    /**
     * @remarks
     * [Required]: The GroupNumber field is required.
     * [GreaterThanZero]: E04710
     * @type number
     * @memberof DraftEntryActivationAsset
     */
    groupNumber: number;
    /**
     * @remarks
     * [Required]: The StartDate field is required.
     * @type CSharpDateTime
     * @memberof DraftEntryActivationAsset
     */
    startDate: CSharpDateTime;
    /**
     * @remarks
     * [Required]: The UsefulLife field is required.
     * [Range]: The field UsefulLife must be between 1 and 600.
     * @type number
     * @memberof DraftEntryActivationAsset
     */
    usefulLife: number;
}

/**
 * @see Economic.InternalApi.Model.DraftEntryActivationAccrual
 *
 * @export
 * @interface DraftEntryActivationAccrual
 */
export interface DraftEntryActivationAccrual {
    accountNumber: number;
    startDate: CSharpDateTime;
    endDate: CSharpDateTime;
}

/**
 * @see Economic.InternalApi.Model.DraftEntry
 *
 * @export
 * @interface DraftEntry
 */
export interface DraftEntry {
    entryId: number;
    entryType: any; // Byte
    date: CSharpDateTime;
    amountInBaseCurrency: number;
    currency: string;
    amount: number;
    journalId: number;
    remainderAmount: number;
    approved: boolean;
    accountId?: number;
    voucherNumber?: number;
    text: string;
    vatCode: string;
    contraAccountId?: number;
    vatCodeContraAccount: string;
    projectId?: number;
    costTypeId?: number;
    customerId?: number;
    supplierId?: number;
    customerInvoiceId?: number;
    dueDate?: CSharpDateTime;
    remainderAmountInBaseCurrency?: number;
    supplierInvoiceId: string;
    invoiceEntryId?: number;
    vatType?: any; // Nullable<Byte>
    vatTypeContraAccount?: any; // Nullable<Byte>
    vatPercentage?: number;
    vatPercentageContraAccount?: number;
    departmentOrDistributionKeyId?: number;
    weekNumber?: any; // Nullable<Byte>
    unit1Id?: number;
    unit2Id?: number;
    quantity?: number;
    quantity2?: number;
    employeeId?: number;
    notUsedDeleteMeFromSchema?: number;
    paymentId: string;
    paymentTypeId?: number;
    preDeductionAmount?: number;
    preDeductionAmountDKK?: number;
    deductionAmount?: number;
    deductionAmountDKK?: number;
    deductionAccountNo?: number;
    exchangeRate?: number;
    vatAmount?: number;
    vatAmountDKK?: number;
    contraVatAmount?: number;
    contraVatAmountDKK?: number;
}

/**
 * @see Economic.InternalApi.Model.DocumentRecipient
 *
 * @export
 * @interface DocumentRecipient
 */
export interface DocumentRecipient {
    id: number;
    /**
     * @remarks
     * [Required]: The Name field is required.
     * @type string
     * @memberof DocumentRecipient
     */
    name: string;
    /**
     * @remarks
     * [Required]: The Email field is required.
     * @type string
     * @memberof DocumentRecipient
     */
    email: string;
}

/**
 * @see Economic.InternalApi.Model.DistributionTemplateDistribution
 *
 * @export
 * @interface DistributionTemplateDistribution
 */
export interface DistributionTemplateDistribution {
    rowNumber?: number;
    /**
     * @remarks
     * [GreaterThanZero]: Account number is not set
     * @type number
     * @memberof DistributionTemplateDistribution
     */
    accountNumber: number;
    amount?: number;
    departmentNumber?: number;
    quantity1?: number;
    quantity2?: number;
    text: string;
    vatCode: string;
    debitCredit: boolean;
    grossNet: boolean;
    dimensionData?: DimensionData[];
}

/**
 * @see Economic.InternalApi.Model.DistributionTemplate
 *
 * @export
 * @interface DistributionTemplate
 */
export interface DistributionTemplate {
    templateNumber?: number;
    /**
     * @remarks
     * [Required]: The Name field is required.
     * @type string
     * @memberof DistributionTemplate
     */
    name: string;
    saveAmount: boolean;
    saveText: boolean;
    saveDepartment: boolean;
    saveQuantity1: boolean;
    saveQuantity2: boolean;
    /**
     * @remarks
     * [ValidateObject]: The field Distributions is invalid.
     * @type DistributionTemplateDistribution[]
     * @memberof DistributionTemplate
     */
    distributions: DistributionTemplateDistribution[];
}

/**
 * @see Economic.InternalApi.Model.DimensionSettings
 *
 * @export
 * @interface DimensionSettings
 */
export interface DimensionSettings {
    showDepartment: boolean;
    showUnit1: boolean;
    showUnit2: boolean;
}

/**
 * @see Economic.InternalApi.Model.DimensionValue
 *
 * @export
 * @interface DimensionValue
 */
export interface DimensionValue {
    number: number;
    name: string;
    access: boolean;
    isDepartment: boolean;
}

/**
 * @see Economic.InternalApi.Model.DimensionLineType
 *
 * @export
 * @enum {number}
 */
export enum DimensionLineTypeEnum {
    DimensionValue = 0,
    DimensionDistribution = 1,
}

/**
 * @see Economic.InternalApi.Model.DimensionLine
 *
 * @export
 * @interface DimensionLine
 */
export interface DimensionLine {
    dimensionId: number;
    key: number;
    name: string;
    active: boolean;
    /**
     * @remarks
     * [JsonConverter]
     * @type DimensionLineTypeEnum
     * @memberof DimensionLine
     */
    type: DimensionLineTypeEnum;
}

/**
 * @see Economic.InternalApi.Model.Dimension
 *
 * @export
 * @interface MultipleDimension
 */
export interface MultipleDimension {
    id: number;
    name: string;
    active: boolean;
    lines: DimensionLine[];
}

/**
 * @see Economic.InternalApi.Model.CustomerGroup
 *
 * @export
 * @interface CustomerGroup
 */
export interface CustomerGroup {
    id: number;
    name: string;
    accountId: number;
    layoutGroupId?: number;
}

/**
 * @see Economic.InternalApi.Model.CurrentUser
 *
 * @export
 * @interface CurrentUser
 */
export interface CurrentUser {
    guid: string;
    agreementNo: number;
    adminAgreementNo: number;
    setupId: number;
    languageId: number;
    enabledAddons: AddonAccess[];
}

/**
 * @see Economic.InternalApi.Model.CostTypeGroup
 *
 * @export
 * @interface CostTypeGroup
 */
export interface CostTypeGroup {
    /**
     * @remarks
     * [Required]: The Number field is required.
     * @type number
     * @memberof CostTypeGroup
     */
    number: number;
    /**
     * @remarks
     * [Required]: The Name field is required.
     * @type string
     * @memberof CostTypeGroup
     */
    name: string;
    /**
     * @remarks
     * [Required]: The Type field is required.
     * @type number
     * @memberof CostTypeGroup
     */
    type?: number;
    markup?: number;
    accounts: CostTypeGroupAccounts;
}

/**
 * @see Economic.InternalApi.Model.CostType
 *
 * @export
 * @interface CostType
 */
export interface CostType {
    /**
     * @remarks
     * [Required]: The Number field is required.
     * @type number
     * @memberof CostType
     */
    number?: number;
    /**
     * @remarks
     * [Required]: The CostGroupNumber field is required.
     * @type number
     * @memberof CostType
     */
    costGroupNumber: number;
    /**
     * @remarks
     * [Required]: The Name field is required.
     * @type string
     * @memberof CostType
     */
    name: string;
    vatCode: string;
    access: boolean;
    lastUpdated: CSharpDateTime;
}

/**
 * Information about the current page of the results.
 * @see Economic.InternalApi.Model.CollectionPagination
 *
 * @export
 * @interface CollectionPagination
 */
export interface CollectionPagination {
    pageSize: number;
    skipPages: number;
    count: number;
    returnAll: boolean;
}

/**
 * Wrapper around the list of results with information about current page.
            Used in endpoints that support pagination.
 * @see Economic.InternalApi.Model.CollectionEnvelope`1
 *
 * @export
 * @interface CollectionEnvelope<T>
*/
export interface CollectionEnvelope<T> {
    data: T[];
    pagination: CollectionPagination;
    totalUnfilteredCount?: number;
}

/**
 * Validation taken from https://github.com/FluentValidation/FluentValidation/blob/main/src/FluentValidation/Validators/ScalePrecisionValidator.cs
 * @see
 *
 * @export
 * @interface T
 */
export interface T {}

/**
 * @see Economic.InternalApi.Model.BookedInvoiceRemainderModel
 *
 * @export
 * @interface BookedInvoiceRemainderModel
 */
export interface BookedInvoiceRemainderModel {
    customerNr: number;
    currency: string;
    remainder: number;
    remainderInBaseCurrency: number;
}

/**
 * @see Economic.InternalApi.Model.BookedEntryText
 *
 * @export
 * @interface BookedEntryText
 */
export interface BookedEntryText {
    entryId: number;
    text: string;
}

/**
 * @see Economic.InternalApi.Model.BookedEntryExtended
 *
 * @export
 * @interface BookedEntryExtended
 */
export interface BookedEntryExtended {
    setupId: number;
    entryNumber: number;
    accountId: number;
    amount: number;
    amountInBaseCurrency: number;
    currencyCode: string;
    date: CSharpDateTime;
    dueDate?: CSharpDateTime;
    text: string;
    type: EntryTypeNamesEnum;
    vatAccountId: string;
    voucherNumber?: number;
    departmentOrDistributionKeyId?: number;
    projectId?: number;
    costType?: number;
    unit1Id?: number;
    unit2Id?: number;
    quantity1?: number;
    quantity2?: number;
    debitorNumber?: number;
    creditorNumber?: number;
    invoiceNumber?: number;
    bookedInvoiceNumber?: number;
    creditorInvoiceNumber: string;
    remainder?: number;
    remainderDefaultCurrency?: number;
    creditorId: string;
    identification: string;
    paymentType?: number;
    isDepartment: boolean;
}

/**
 * @see Economic.InternalApi.Model.BookedEntryDepartment
 *
 * @export
 * @interface BookedEntryDepartment
 */
export interface BookedEntryDepartment {
    entryId: number;
    departmentId?: number;
    dimensionData?: DimensionData[];
}

/**
 * @see Economic.InternalApi.Model.BookedEntry
 *
 * @export
 * @interface BookedEntry
 */
export interface BookedEntry {
    setupId: number;
    entryId: number;
    accountId: number;
    amount: number;
    amountInBaseCurrency: number;
    currency: string;
    date: CSharpDateTime;
    entryType: any; // Byte
    text: string;
    voucherNumber: number;
    debitorCreditorNumber: number;
    vatCode: string;
    projectId?: number;
    departmentOrDistributionKeyId?: number;
    dimensionData?: DimensionData[];
}

/**
 * @see Economic.InternalApi.Model.AssetTransaction
 *
 * @export
 * @interface AssetTransaction
 */
export interface AssetTransaction {
    assetNumber: number;
    voucherNumber?: number;
    invoiceDate: CSharpDateTime;
    text: string;
    accountNumber: number;
    amount: number;
    numMonths?: number;
}

/**
 * @see Economic.InternalApi.Model.Dimensions.DimensionData
 *
 * @export
 * @interface DimensionData
 */
export interface DimensionData {
    dimensionId: number;
    dimensionKey?: number;
}

/**
 * @see Economic.InternalApi.Model.AssetGroup
 *
 * @export
 * @interface AssetGroup
 */
export interface AssetGroup {
    /**
     * @remarks
     * [Required]: The Number field is required.
     * [GreaterThanZero]: E04710
     * @type number
     * @memberof AssetGroup
     */
    number: number;
    /**
     * @remarks
     * [Required]: The Name field is required.
     * [MaxLength]: The field Name must be a string or array type with a maximum length of '100'.
     * @type string
     * @memberof AssetGroup
     */
    name: string;
    /**
     * @remarks
     * [Range]: The field ResidualValue must be between 1 and 100.
     * @type number
     * @memberof AssetGroup
     */
    residualValue?: number;
    /**
     * @remarks
     * [Required]: The UsefulLife field is required.
     * [Range]: The field UsefulLife must be between 1 and 600.
     * @type number
     * @memberof AssetGroup
     */
    usefulLife: number;
    /**
     * @remarks
     * [Required]: The AdditionsAccount field is required.
     * [AccountNumber]: E07150
     * @type number
     * @memberof AssetGroup
     */
    additionsAccount: number;
    /**
     * @remarks
     * [Required]: The DepreciationBalanceSheetAccount field is required.
     * [AccountNumber]: E07150
     * @type number
     * @memberof AssetGroup
     */
    depreciationBalanceSheetAccount: number;
    /**
     * @remarks
     * [Required]: The DepreciationProfitAndLossAccount field is required.
     * [AccountNumber]: E07150
     * @type number
     * @memberof AssetGroup
     */
    depreciationProfitAndLossAccount: number;
    /**
     * @remarks
     * [Required]: The DisposalsAccount field is required.
     * [AccountNumber]: E07150
     * @type number
     * @memberof AssetGroup
     */
    disposalsAccount: number;
    /**
     * @remarks
     * [Required]: The ReversedDepreciationAccount field is required.
     * [AccountNumber]: E07150
     * @type number
     * @memberof AssetGroup
     */
    reversedDepreciationAccount: number;
    /**
     * @remarks
     * [Required]: The NetBookedValueAccount field is required.
     * [AccountNumber]: E07150
     * @type number
     * @memberof AssetGroup
     */
    netBookedValueAccount: number;
    /**
     * @remarks
     * [Required]: The AdditionsContraAccount field is required.
     * [AccountNumber]: E07150
     * @type number
     * @memberof AssetGroup
     */
    additionsContraAccount: number;
    /**
     * @remarks
     * [Required]: The DepreciationContraAccount field is required.
     * [AccountNumber]: E07150
     * @type number
     * @memberof AssetGroup
     */
    depreciationContraAccount: number;
}

/**
 * @see Economic.InternalApi.Model.Asset
 *
 * @export
 * @interface Asset
 */
export interface Asset {
    number: number;
    groupNumber: number;
    name: string;
    entryNumber?: number;
    departmentNumber?: number;
    purchaseAmountDkk: number;
    purchaseAmount: number;
    currency: string;
    scrapValue?: number;
    invoiceDate: CSharpDateTime;
    startDate: CSharpDateTime;
    lifetime: number;
    startDepreciated?: number;
    depreciated?: number;
    startDepreciatedMonths?: number;
    depreciatedMonths?: number;
    latestDepreciatedMonths?: CSharpDateTime;
    salesDate?: CSharpDateTime;
    salesPrice?: number;
    bookedValue?: number;
    profit?: number;
    depreciationBasis?: number;
    notDepreciable?: number;
    inactive: boolean;
    depreciatedUntilPeriodNumber?: number;
    isSold: boolean;
    description: string;
    created: CSharpDateTime;
}

/**
 * @see Economic.InternalApi.Model.AdministratorAccessRequest
 *
 * @export
 * @interface AdministratorAccessRequest
 */
export interface AdministratorAccessRequest {
    adminAgreementNumber: number;
    clientAgreementNumber: number;
    expiry?: CSharpDateTime;
    setAsPayer: boolean;
}

/**
 * @see Economic.InternalApi.Model.AdministratorLicenseProvisionGrant
 *
 * @export
 * @interface AdministratorLicenseProvisionGrant
 */
export interface AdministratorLicenseProvisionGrant {
    agreementNumber: number;
    licenseId: number;
    parentLicenseId?: number;
    licenseName: string;
    pricePerMonth?: number;
    pricePerMonthExpiry?: CSharpDateTime;
    pricePerMonthApplyType?: AdministratorLicenseDiscountApplyType;
    pricePerMonthText?: string;
    discountPercentage?: number;
    discountPercentageExpiry?: CSharpDateTime;
    discountPercentageApplyType?: AdministratorLicenseDiscountApplyType;
    discountPercentageText?: string;
    access?: boolean;
}

export type AdministratorLicenseProvisionGrantFormFriendly = Omit<
    AdministratorLicenseProvisionGrant,
    'pricePerMonthExpiry' | 'discountPercentageExpiry'
> & {
    pricePerMonthExpiry: Date | undefined;
    discountPercentageExpiry: Date | undefined;
};

export enum AdministratorLicenseDiscountApplyType {
    All = 0,
    AdministratorPaid = 1,
}

export enum ReturnToCompanyView {
    Free = 0,
    Active = 1,
    Barred = 2,
    ActiveTrial = 4,
    Deregistered = 5,
}

/**
 * @see Economic.InternalApi.Model.AgreementLicense
 *
 * @export
 * @interface AgreementLicense
 */
export interface AgreementLicense {
    id: number;
    agreementNumber: number;
    licenseId: number;
    licenseName: string;
    registered: CSharpDateTime;
    deregistered?: CSharpDateTime;
    pricePerMonth?: number;
    pricePerMonthExpiry?: CSharpDateTime;
    pricePerMonthText?: string;
    discountPercentage?: number;
    discountPercentageExpiry?: CSharpDateTime;
    discountPercentageText?: string;
    allowDiscountUpdates: boolean;
    discountDerivedFromAgreementName?: string;
}

/**
 * @see Economic.InternalApi.Model.UpdateAgreementLicenseRequest
 *
 * @export
 * @interface UpdateAgreementLicenseRequest
 */
export interface UpdateAgreementLicenseRequest {
    id: number;
    agreementNumber: number;
    discountPercentage?: number;
    discountPercentageExpiry?: Date | undefined;
    discountPercentageText?: string;
    pricePerMonth?: number;
    pricePerMonthExpiry?: Date | undefined;
    pricePerMonthText?: string;
}

/**
 * @see Economic.InternalApi.Model.AdministratorAccessDeleteRequest
 *
 * @export
 * @interface AdministratorAccessDeleteRequest
 */
export interface AdministratorAccessDeleteRequest {
    adminAgreementNumber: number;
    clientAgreementNumber: number;
}

/**
 * @see Economic.InternalApi.Model.AdminAccess
 *
 * @export
 * @interface AdminAccess
 */
export interface AdminAccess {
    adminAgreementNumber: number;
    adminUserId: string;
    expires: string;
}

/**
 * @see Economic.InternalApi.Model.AccountingYearResponse
 *
 * @export
 * @interface AccountingYearResponse
 */
export interface AccountingYearResponse {
    closed: boolean;
    configuration: number;
    numberOfEntries: number;
    from: CSharpDateTime;
    hasNewEntries: boolean;
    ignoreEntryLimit: boolean;
    to: CSharpDateTime;
    year: string;
    hasValue: boolean;
    periods: PeriodResponse[];
}

/**
 * @see Economic.InternalApi.Model.Account
 *
 * @export
 * @interface Account
 */
export interface Account {
    /**
     * @remarks
     * [Required]: The Type field is required.
     * @type number
     * @memberof Account
     */
    type: number;
    /**
     * @remarks
     * [Required]: The Number field is required.
     * @type number
     * @memberof Account
     */
    number: number;
    /**
     * @remarks
     * [Required]: The Name field is required.
     * @type string
     * @memberof Account
     */
    name: string;
    vatCode: string;
    accounts: AccountAccounts;
    keyFigureCode?: number;
    /**
     * @remarks
     * [Required]: The DebitCredit field is required.
     * @type boolean
     * @memberof Account
     */
    debitCredit: boolean;
    /**
     * @remarks
     * [Required]: The Access field is required.
     * @type boolean
     * @memberof Account
     */
    access: boolean;
    isBlockedForDirectEntries: boolean;
    totalIntervals?: string;
    totalFromDisplayValue: string;
    dimension: AccountDimension;
    dimensions: AccountMultipleDimension[];
    balance?: number;
    reference1: string;
    currency: string;
    assetGroupNumber: number;
    standardAccountId?: number;
    displayNumber?: string;
}

/**
 * @see Economic.InternalApi.Endpoints.NestedValidationResults
 *
 * @export
 * @interface NestedValidationResults
 */
export interface NestedValidationResults {
    nestedResults: any[]; // IList<ValidationResult>
}

/**
 * @see Economic.InternalApi.Endpoints.ValidateObjectAttribute
 *
 * @export
 * @interface ValidateObjectAttribute
 */
export interface ValidateObjectAttribute {}

/**
 *
 */
export enum UserTypeForBilling {
    NotBilled = 0,

    TimeLogger = 1,

    ProjectManager = 2,

    MobileTimelogger = 3,

    RegularUser = 4,

    SuperUser = 5,

    MobileExpense = 6,
}

/**
 *
 */
export enum EmployeeType {
    NotEmployee = 0,

    TimeLogger = 1,

    ProjectManager = 2,

    MobileTimeLogger = 3,
}

/**
 *
 */
export interface ProfileDto {
    userTypeForBilling: UserTypeForBilling;
    employeeType: EmployeeType;
    userType: string;
    id: string;
    userId: string;
    agreementId: number;
    employeeId?: number;
    approved: boolean;
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    signUpDate: CSharpDateTime;
    deregisteredDate?: CSharpDateTime;
    language: number;
    countryCode: string;
    tabShownPrinciple: number;
    startTab?: number;
    tabOrder: string;
    monthlyUserPrice: number;
    hasAccessSettings: boolean;
    hasAccessJournals: boolean;
    hasAccessByDefaultToCompanies: boolean;
    hasAccessApproveEntries: boolean;
    hasJoinedVismaConnect: boolean;
    hasLoggedInUsingVismaConnect: boolean;
    password: string;

    hasAccessAccounting: boolean;
    hasAccessInvoice: boolean;
    hasAccessReport: boolean;
    hasAccessProject: boolean;
    hasAccessInventory: boolean;
    hasAccessModifyReportCoverTemplates: boolean;
    hasAccessProjectTimeLogger: boolean;
    hasAccessProjectMobileTimeLogger: boolean;
    hasAccessProjectManager: boolean;
    hasAccessAbleToSignReports: boolean;
    canEditAdminUsers: boolean;

    disableInlineSearch: boolean;

    showGlobalCustomerRating: boolean;
    hasConsentToRateCustomer: boolean;
    hideMarginAndCostPriceInSales: boolean;

    lastUsedDate?: CSharpDateTime;
    isSuperUser: boolean;
    copyFromExampleSuperUser: boolean;
    note: string;
    isApprover: boolean;

    vismaUserId: string;
    position: string;
    type?: number;
}

/**
 *
 */
export interface Permissions {
    canChangeUserId: boolean;
    canChangeName: boolean;
    canChangeEmail: boolean;
    canChangeDeregisteredDate: boolean;
    canChangeIsApproved: boolean;
    canChangeLanguage: boolean;
    canChangeStartTab: boolean;
    canChangeTabShownPrinciple: boolean;
    canChangeEmployee: boolean;
    canChangeInlineSearchConfiguration: boolean;
    canChangeMarginAndCostPriceInSalesConfiguration: boolean;
    canChangeAccessJournals: boolean;
    canChangeAccessVismaRatings: boolean;
    canChangeAccessSettings: boolean;
    canChangeAccessApproveEntries: boolean;
    canChangeAccessByDefaultToCompanies: boolean;
    canChangeAccessConnect: boolean;
    canChangeAccessModifyReportCoverTemplates: boolean;
    canChangePosition: boolean;
    canChangeAbleToSignReports: boolean;
    canChangeEditAdminUsers: boolean;

    canChangeAccessAccounting: boolean;
    canChangeAccessInvoice: boolean;
    canChangeAccessReport: boolean;
    canChangeAccessProject: boolean;
    canChangeAccessInventory: boolean;

    canChangePassword: boolean;
    canJoinConnect: boolean;
    canInviteConnect: boolean;
    canSubscribeToNewsletter: boolean;
    enforceVismaConnectFields: boolean;
    canSeeVismaUserId: boolean;
    canGoToAccountSettings: boolean;
    canChangeUserType: boolean;
}

/**
 * @see Economic.InternalApi.Model.SmartPay.SmartPayPaymentInvoiceModel
 *
 * @export
 * @interface SmartPayPaymentInvoiceModel
 */
export interface SmartPayPaymentInvoiceModel {
    journalEntryPaymentId?: number;
    smartPayPaymentId?: number;
    attachmentNumber?: number;
    attachmentPages: DocumentPageDto[];
    dueDate: CSharpDateTime;
    invoiceNumber: string;
    invoiceAmount: number;
    invoiceRemainingAmount: number;
    draftRemainingAmount: number;
    amountToBePaid: number;
    reconciliationAmount: number;
    paymentAmount?: number;
    paidAmount?: number;
    paymentSupplierInvoiceIdentifier: string;
    invoiceCurrency: string;
    baseCurrency: string;
    exchangeRate: number;
    novemberFirstExchangeRate?: number;
    paymentSourceTypeId: number;
    paymentSourceId: number;
}

/**
 * @see Economic.Dtos.DocumentPageDto
 *
 * @export
 * @interface DocumentPageDto
 */
export interface DocumentPageDto {
    id: number;
    documentNumber: number;
    pageNumber: number;
    imageUrl: string;
    thumbnailUrl: string;
    imageDataKey: string;
    imageStorageId: number;
}

/**
 * @see Economic.InternalApi.Model.SmartPay.SmartPayPaymentInfoModel
 *
 * @export
 * @interface SmartPayPaymentInfoModel
 */
export interface SmartPayPaymentInfoModel {
    combineInvoices: boolean;
    feeAccount?: number;
    paymentAccount?: number;
    daybook?: number;
    appUrl: string;
}

/**
 * @see Economic.InternalApi.Model.SmartPay.SmartPayPaymentExtendedModel
 *
 * @export
 * @interface SmartPayPaymentExtendedModel
 */
export interface SmartPayPaymentExtendedModel extends SmartPayCandidatePayment {
    journalId?: number;
    draftEntryId?: number;
    bookedEntryId?: number;
    journalEntryPaymentId?: number;
    paymentSupplierInvoiceIdentifier: string;
    paymentSupplierIdentifier: string;
    smartPayPaymentId?: number;
    paymentDate: CSharpDateTime;
    dueDate: CSharpDateTime;
    suggestedPaymentDate?: CSharpDateTime;
    invoiceNumber: string;
    attachmentNumber?: number;
    supplierName: string;
    supplierId: number;
    supplierCountry: string;
    paymentTypeId: number;
    invoiceAmount: number;
    invoiceRemainingAmount: number;
    draftRemainingAmount: number;
    amountToBePaid: number;
    amountInBaseCurrency: number;
    reconciliationAmount: number;
    paymentAmount?: number;
    paidAmount?: number;
    actualPaidAmount?: number;
    invoiceTotalInBaseCurrency: number;
    invoiceCurrency: string;
    exchangeRate: number;
    novemberFirstExchangeRate?: number;
    balance: number;
    baseCurrency: string;
    isDaybook: boolean;
    status: PaymentStatuses;
    paymentReference: string;
    invoiceText: string;
    fee: number;
    paymentAccountId?: number;
    senderBankName: string;
    senderBankAccount: string;
    validationMessages: string[];
    postValidationMessages: string[];
    invoices: SmartPayPaymentInvoiceModel[];
    invoicesCount: number;
    createdDate?: CSharpDateTime;
    createdByEmail: string;
    rejectedDate?: CSharpDateTime;
    rejectedByEmail: string;
    takenBackDate?: CSharpDateTime;
    takenBackByEmail: string;
    isImported: boolean;
    isMarkedAsPaid: boolean;
    settlementDate?: CSharpDateTime;
    supplierGroupId: number;
    voucherNumber?: number;
    executionDate?: CSharpDateTime;
    hasJournalEntries: boolean;
    paymentSourceTypeId: SmartPayPaymentSourceType;
    paymentSourceId: number;
}

/**
 * @see Economic.InternalApi.Model.SmartPay.SmartPayPaymentExtendedDetailsModel
 *
 * @export
 * @interface SmartPayPaymentExtendedDetailsModel
 */
export interface SmartPayPaymentExtendedDetailsModel {
    approvals: Approval[];
    rejectionReason: string;
    rejectedByEmail: string;
    rejectionDate?: CSharpDateTime;
    inititationStatus: string;
    externalPartnerUserName: string;
    errorMessage: string;
    fromAmount: number;
    recipientFeeCurrencyCode: string;
    recipientFeeAmount: number;
    fastPaymentFeeAmount: number;
    fastPaymentFeeCurrencyCode: string;
    settlementAmount: number;
    exchangeRate: number;
    settlementAccountNumber: string;
    settlementBicCode: string;
    latestSettlementDate?: CSharpDateTime;
    settlementDate?: CSharpDateTime;
    payerStreet1: string;
    payerStreet2: string;
    payerPostCode: string;
    payerCity: string;
    payerCountryCode: string;
    payerCountry: string;
    recipientStreet1: string;
    recipientStreet2: string;
    recipientPostCode: string;
    recipientCity: string;
    recipientCountryCode: string;
    recipientCountry: string;
    executionDate?: CSharpDateTime;
}

/**
 * @see Economic.Dto.SmartPay.Approval
 *
 * @export
 * @interface Approval
 */
export interface Approval {
    approvedByEmail: string;
    approvalDate?: CSharpDateTime;
}

/**
 * @see Economic.InternalApi.Model.SmartPay.SmartPayPaymentActionResultModel
 *
 * @export
 * @interface SmartPayPaymentActionResultModel
 */
export interface SmartPayPaymentActionResultModel {
    journalId?: number;
    draftEntryId?: number;
    bookedEntryId?: number;
    journalEntryPaymentId?: number;
    smartPayPaymentId?: number;
    paymentReference: string;
    errorMessages: string[];
    paymentSourceTypeId: SmartPayPaymentSourceType;
    paymentSourceId: number;
}

/**
 * @see Economic.InternalApi.Model.SmartPay.SmartPayPaymentAccountModel
 *
 * @export
 * @interface SmartPayPaymentAccountModel
 */
export interface SmartPayPaymentAccountModel {
    number?: number;
    name: string;
    useInSmartPay: boolean;
    account: string;
    bankName: string;
    autoWithdrawAppliedDate?: CSharpDateTime;
    autoWithdrawActiveDate?: CSharpDateTime;
    hasNetMandate: boolean;
    isOnNovemberFirst: boolean;
    economicDraftAccount: string;
    novemberFirstDraftAccount: string;
    novemberFirstBankName: string;
    status: string;
}

/**
 * @see Economic.InternalApi.Model.SmartPay.SmartPayPaymentResultModel
 *
 * @export
 * @interface SmartPayPaymentResultModel
 */
export interface SmartPayPaymentResultModel {
    paymentReference: string;
    errorMessages: ErrorMessage[];
}

export interface ErrorMessage {
    message: string;
    value: string;
}

/**
 * @see Economic.InternalApi.Model.SmartPay.SmartPayApprovePaymentResultModel
 *
 * @export
 * @interface SmartPayApprovePaymentResultModel
 */
export interface SmartPayApprovePaymentResultModel {
    approveUrl: string;
    invalidPayments: SmartPayPaymentResultModel[];
}

/**
 * @see Economic.InternalApi.Model.SmartPay.SmartPayPaymentCountByStatusModel
 *
 * @export
 * @interface SmartPayPaymentCountByStatusModel
 */
export interface SmartPayPaymentCountByStatusModel {
    status: string;
    count: number;
    total: number;
}

/**
 *
 */
export interface UserPopupViewModel {
    configuration: UserPopupConfigurationViewModel;
    profile: ProfileDto;
}

/**
 *
 */
export interface UserPopupConfigurationViewModel {
    userType: string;
    confirmationText: string;
    displayGoToConnectColumn: boolean;
    goToConnectLink: string;
    languages: any[]; // List<KeyValuePair<number, string>>
    startTabs: any[]; // List<KeyValuePair<number, string>>
    tabShownPrinciples: any[]; // List<KeyValuePair<String, String>>
    permissions: Permissions;
    userTypes: any[]; // List<KeyValuePair<number, string>>;
}

/**
 * @see Economic.InternalApi.Model.SupplierGroupInfoModel
 *
 * @export
 * @interface SupplierGroupInfoModel
 */
export interface SupplierGroupInfoModel {
    hasDeductions: boolean;
}

export interface FormFieldDto {
    formFieldId: number;
    formFieldTypeId: number;
    textId: string;
    defaultTabOrder: number;
    activeOnForm: boolean;
    defaultFormTabId: number;
    editionId: number | undefined;
    formFieldAccessTypeId: number | undefined;
}

export interface UserTabOrder {
    disabled: boolean;
    id: string;
    userTabOrderListId: number | undefined;
    tabOrder: number;
    formFieldSizeId: number | undefined;
    formTabId: number;
    formFieldId: number;
    skip: boolean | undefined;
    lockSize: boolean | undefined;
    lockSkip: boolean | undefined;
}

/**
 * @see Economic.InternalApi.Model.Entries.EntryOnAccountListView
 *
 * @export
 * @interface EntryOnAccountListView
 */
export interface EntryOnAccountListView {
    setupId: number;
    entryId: number;
    booked: boolean;
    entryType: EntryTypeNamesEnum | EntryType;
    date: CSharpDateTime;
    voucherId: number;
    customerInvoiceId: number | undefined;
    supplierInvoiceId: number | undefined;
    text: string;
    currency: string;
    amount: number;
    amountInBaseCurrency: number;
    balance: number;
    dueDate: CSharpDateTime;
    remainderAmount: number;
    remainderAmountInBaseCurrency: number;
    deresRef: string;
    invoiceDueDate: CSharpDateTime;
    hasReminder: boolean;
    attachmentNumber: number | undefined;
    inventoryNumber: number | undefined;
}

/**
 * @see Economic.InternalApi.Model.ElectronicInvoicing.PeppolSendingCapability
 *
 * @export
 * @interface PeppolSendingCapability
 */
export interface PeppolSendingCapability {
    canSend: boolean;
}

/**
 * @see Economic.InternalApi.Model.Entries.MatchedEntryOnAccountListView
 *
 * @export
 * @interface MatchedEntryOnAccountListView
 */
export interface MatchedEntryOnAccountListView {
    matchingId: number;
    setupId: number;
    entryType: EntryTypeNamesEnum;
    date: CSharpDateTime;
    voucherId: number;
    text: string;
    invoiceId: string;
    amount: number;
    matchedAmount: number;
    matchedAmountInBaseCurrency: number;
    attachmentNumber: number | undefined;
}

export interface UserTabOrderList {
    userTabOrderListId: number;
    userTabOrderName: string;
}

export interface CountryFieldOrder {
    id: string;
    listNumber: number;
    ordinal?: number;
}

export interface PriceGroup {
    id: number;
    name: string;
}

export interface LanguageDropdownOptionModel {
    id: string;
    text: string;
}

export interface ProductSpecialPriceListView {
    productNumber: string;
    productName: string;
    priceGroupId?: number;
    productSpecialPriceId?: number;
    salesPrice?: number;
    recommendedPrice?: number;
    costPrice?: number;
    specialPriceBaseCurrency?: number;
    specialPrice?: number;
    specialPriceCurrency: string;
    discountPercentage?: number;
}

export interface ProductSpecialPriceCurrencyModel {
    productSpecialPriceId: number;
    currency: string;
    salesPrice: number;
}

export interface CreateSalesDocumentLinesFromProductCollectionModel {
    salesDocumentId: number;
    productCollectionId: number;
    quantity: number;
    addCollectionTitleLine: boolean;
    addCollectionTotalPriceLine: boolean;
}
/**
 * @see Economic.Accounting.Dto.PaymentFormatDto
 *
 * @export
 * @interface PaymentFormatDto
 */
export interface PaymentFormatDto {
    canSendElectronically: boolean;
    editionId?: number;
    name: string;
    hasValue: boolean;
    isXtl: boolean;
    number?: number;
    octopusEndpoint: string;
    transformFile: string;
    fileNameTemplate: string;
}

/**
 * @see Economic.Accounting.Dto.PaymentAccountWithIntegrationCurrencyDto
 *
 * @export
 * @interface PaymentAccountWithIntegrationCurrencyDto
 */
export interface PaymentAccountWithIntegrationCurrencyDto {
    paymentAccount: PaymentAccountDto;
    integrationCurrency: string;
}

/**
 * @see Economic.Accounting.Dto.PaymentAccountDto
 *
 * @export
 * @interface PaymentAccountDto
 */

export interface PaymentAccountDto {
    bankAccountNumber: string;
    bankAgreementNumber: string;
    bankRegistrationNumber: string;
    format: PaymentFormatDto;
    hasValue: boolean;
    name: string;
    number: number;
    bankName: string;
    octopusSalt: string;
    octopusToken: string;
    oneTimeSetPasswordUrl: string;
    setupId: number;
    bankIntegrationId?: number;
    iban: string;
    bic: string;
    bankCurrency: string;
    useInSmartPay: boolean;
    pbsNo: string;
    pbsCustGrNo: number;
    giroAccount: string;
    fiSupplierNo: string;
    note: string;
}

/**
 * @see Economic.BankIntegration.Legacy.Dto.Bank.BankDto
 *
 * @export
 * @interface BankDto
 */
export interface BankDto {
    id: number;
    name: string;
    countryCode: string;
    swift: string;
    aiiaProviderIds: string[];
    novemberFirstId: string;
    novemberFirstNames: string[];
    formatNumber?: number;
    provider: string;
    validateDKNumberFormat: boolean;
    isTest: boolean;
}

export interface PartialInvoiceDto {
    invoiceId: number;
    date: CSharpDateTime;
    booked: boolean;
    amount: number;
}

export interface CreatePartialInvoiceRequest {
    salesDocumentId: number;
    productId: string;
    isFixedAmount: boolean;
    amount: number;
}

export interface PartialInvoiceDistributionInfoDto {
    distributionType?: PartialInvoiceDistributionType;
}

export enum PartialInvoiceDistributionType {
    AmountBased,
    ProductLineBased,
}

export interface PartialInvoicingViewLineDto {
    lineId: number;
    sortKey: number;
    productId: string;
    productName: string;
    quantity: number;
    alreadyInvoiced: number;
    newQuantity?: number;
}

export interface Webhook {
    id: number;
    name: string;
    typeId: number;
    // Not returned by internal-api, but set by the client
    typeName: string;
    url: string;
    postData: string;
    contentType: string;
    active: boolean;
    readOnly: boolean;
    mergeFields: string;
}

export interface WebhookType {
    id: number;
    name: string;
}

export interface WebhookTypeMergeField {
    typeId: number;
    mergeFields: MergeField[];
}

export interface MergeField {
    name: string;
    description: string;
}

/**
 * @see Economic.Dto.Addons.AddonInfoDto
 *
 * @export
 * @interface AddonInfoDto
 */

export interface AddonInfoDto {
    id: number;
    pricePerMonth: number;
    priceAfterDiscountPerMonth: number;
    enabled: boolean;
    canToggle?: boolean;
    togglePermission?: boolean;
    warningText: string;
    discount?: number;
    dateTime?: Date;
}

/**
 * @see Economic.InternalApi.Model.Inbox.AutoSuggestTrainingData
 *
 * @export
 * @interface AutoSuggestTrainingData
 */

export interface AutoSuggestTrainingData {
    documentType: AutoSuggestFlamelDataSetTypes;
    accountNumber?: number;
    contraAccountNumber?: number;
}
